import React, { useState, useEffect, useRef } from 'react';
import { FiArrowUpRight, FiArrowDownRight, FiList, FiX, FiCheck, FiSettings, FiMenu, FiUsers, FiMail, FiCheckSquare, FiCalendar, FiClock, FiFileText, FiPlus, FiSearch, FiChevronLeft, FiChevronRight, FiMoreHorizontal } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { motion, Reorder, AnimatePresence } from 'framer-motion';
import { GridLayout, useContainerWidth } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import axios from 'axios';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  LabelList,
  Sector
} from 'recharts';
import './Dashboard.css';
import API_ENDPOINTS from '../config/apiEndpoints';
import CustomDashboard from './CustomDashboard';
import Loader from '../components/Loader/Loader';
import MailSettingsModal from '../components/common/MailSettingsModal';
import MailReportModal from '../components/common/MailReportModal';
import SubHeader from '../components/common/SubHeader/SubHeader';
import MediaLibrary from '../components/MediaLibrary';
import usePermissions from './Master_Settings/usePermissions';
import useFieldMasking from './Master_Settings/useFieldMasking';
import useHierarchy from './Master_Settings/useHierarchy';

const ThreeGearsIcon = ({ size = 20 }) => (
  <div className="relative flex items-center justify-center" style={{ width: size * 1.4, height: size * 1.4 }}>
    <FiSettings size={size} className="absolute left-0" />
    <div className="flex flex-col gap-0.5 ml-auto">
      <FiSettings size={size * 0.55} />
      <FiSettings size={size * 0.55} />
    </div>
  </div>
);

const PipelineChart = ({
  title,
  data,
  xKey,
  yKey,
  yAxisLabel = "",
  legendLabel = "",
  barColor = null
}) => {
  const chartColors = [
    '#3498db', '#e91e63', '#5dade2', '#f44336', '#5b2c6f', '#2c3e50',
    '#f1c40f', '#a04000', '#d35400', '#17202a', '#2ecc71', '#1abc9c',
    '#27ae60', '#117a65', '#58d68d', '#e67e22', '#f39c12', '#c0392b',
    '#922b21', '#a29bfe', '#0984e3', '#2980b9', '#76d7c4'
  ];

  const isDense = data && data.length > 15;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const barData = payload[0].payload;
      const breakdown = barData.breakdown || [];

      return (
        <div className="pipeline-custom-tooltip">
          <div className="tooltip-header">{label}</div>
          <div className="tooltip-content">
            {breakdown.length > 0 ? (
              breakdown.map((item, idx) => (
                <div key={idx} className="tooltip-item">
                  <span className="tooltip-label" style={{ color: item.color || chartColors[idx % chartColors.length] }}>
                    {item.name}:
                  </span>
                  <span className="tooltip-value">
                    {parseFloat(item.value || 0).toFixed(1)} mm
                  </span>
                </div>
              ))
            ) : (
              <div className="tooltip-item">
                <span className="tooltip-label" style={{ color: '#3b82f6' }}>
                  {legendLabel || 'Value'}:
                </span>
                <span className="tooltip-value">
                  {parseFloat(payload[0].value || 0).toFixed(1)} mm
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="pipeline-chart-card">
      <div className="pipeline-chart-header">
        <h3 className="pipeline-chart-title">{title}</h3>
      </div>
      <div className="pipeline-chart-recharts-container" style={{ width: '100%', height: '500px' }}>
        {data && data.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 40, right: 30, left: 20, bottom: 80 }}
              barCategoryGap="10%"
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#f1f5f9" />
              <XAxis
                dataKey={xKey}
                angle={-45}
                textAnchor="end"
                interval={isDense ? 'preserveStartEnd' : 0}
                height={80}
                fontSize={9}
                tick={{ fill: '#64748b' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                fontSize={10}
                tick={{ fill: '#64748b' }}
                axisLine={false}
                tickLine={false}
                label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', offset: -10, style: { textAnchor: 'middle', fontSize: '11px', fontWeight: 600, fill: '#64748b' } } : null}
              />
              <Tooltip
                cursor={{ fill: '#f8fafc' }}
                content={<CustomTooltip />}
              />
              <Bar
                dataKey={yKey}
                radius={[4, 4, 0, 0]}
                barSize={isDense ? 15 : 30}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={barColor || chartColors[index % chartColors.length]} />
                ))}
                <LabelList
                  dataKey={yKey}
                  position="top"
                  style={{ fill: '#1e293b', fontSize: 9, fontWeight: 700 }}
                  offset={10}
                />
              </Bar>
              <Legend
                verticalAlign="bottom"
                align="center"
                content={({ payload }) => (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '20px',
                    marginTop: '20px',
                    padding: '10px',
                    borderTop: '1px solid #f1f5f9'
                  }}>
                    {legendLabel ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: '#1e293b', fontWeight: 600 }}>
                        <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: barColor || chartColors[0], flexShrink: 0 }} />
                        <span>{legendLabel}</span>
                      </div>
                    ) : (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                        gap: '8px',
                        width: '100%',
                        maxHeight: '80px',
                        overflowY: 'auto',
                        scrollbarWidth: 'thin'
                      }}>
                        {(payload || []).map((entry, index) => (
                          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '9px', color: '#64748b' }}>
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: entry.color, flexShrink: 0 }} />
                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{entry.value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="pipeline-no-data">No data available</div>
        )}
      </div>
    </div>
  );
};
const AVATAR_COLORS = [
  '#1e3a8a', // vibrant deep blue
  '#7c3aed', // vibrant violet
  '#10b981', // emerald green
  '#f97316', // vibrant orange
  '#eab308', // bright yellow
  '#ec4899', // pink
  '#06b6d4', // cyan
];

const getInitialsByString = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].substring(0, 2).toUpperCase();
};

const getSafeVal = (obj, keys, defaultVal = '---') => {
  if (!obj || typeof obj !== 'object') return defaultVal;

  // 1. Direct case-sensitive check
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null && String(obj[k]).trim() !== '') return String(obj[k]).trim();
  }

  // 2. Case-insensitive check
  const lowerKeys = keys.map(k => k.toLowerCase().trim());
  const objKeys = Object.keys(obj);

  for (const key of objKeys) {
    if (lowerKeys.includes(key.toLowerCase().trim())) {
      if (obj[key] !== undefined && obj[key] !== null && String(obj[key]).trim() !== '') return String(obj[key]).trim();
    }
  }

  return defaultVal;
};

const CustomWidgetContent = ({ tile, data, pieData, maskValue, onTileClick, onListItemClick }) => {
  const [activePieIndex, setActivePieIndex] = useState(-1);
  const name = (tile.ReportName || '').trim();
  const sectionName = tile.SectionName || '';
  const value = tile.Value;
  // 1. Source Wise Lead Distribution (Pie Chart)
  if (name.includes("Source Wise") || name.toLowerCase().includes("sourcewise") || name.includes("Distribution")) {
    const COLORS = [
      '#1e3a8a', '#7c3aed', '#10b981', '#f97316', '#eab308',
      '#ec4899', '#06b6d4', '#6366f1', '#14b8a6', '#f43f5e'
    ];
    const chartData = Array.isArray(pieData) ? pieData : [];
    const total = chartData.reduce((sum, entry) => sum + (entry.value || 0), 0);

    return (
      <div
        className="custom-pie-body"
        onClick={() => onTileClick && onTileClick(tile)}
        style={{ cursor: onTileClick ? 'pointer' : 'default' }}
      >
        <label className="custom-widget-label">{name}</label>
        <div className="custom-pie-container">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="30%"
                  cy="50%"
                  innerRadius={0}
                  outerRadius={50}
                  paddingAngle={0}
                  dataKey="value"
                  label={false}
                  activeIndex={activePieIndex}
                  activeShape={(props) => {
                    const RADIAN = Math.PI / 180;
                    const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
                    const sin = Math.sin(-RADIAN * midAngle);
                    const cos = Math.cos(-RADIAN * midAngle);
                    const sx = cx + (outerRadius + 5) * cos;
                    const sy = cy + (outerRadius + 5) * sin;
                    return (
                      <g>
                        <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 5} startAngle={startAngle} endAngle={endAngle} fill={fill} />
                      </g>
                    );
                  }}
                  onMouseEnter={(_, index) => setActivePieIndex(index)}
                  onMouseLeave={() => setActivePieIndex(-1)}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend
                  layout="vertical"
                  verticalAlign="middle"
                  align="right"
                  width="65%"
                  wrapperStyle={{
                    paddingLeft: '5px',
                    fontSize: '10px',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    maxHeight: '120px',
                    right: -10
                  }}
                  onMouseEnter={({ payload }, index) => setActivePieIndex(index)}
                  onMouseLeave={() => setActivePieIndex(-1)}
                  formatter={(value, entry) => {
                    const val = entry.payload.value;
                    const percent = total > 0 ? ((val / total) * 100).toFixed(1) : 0;
                    return (
                      <span style={{ color: '#475569', fontWeight: 600, fontSize: '9px', whiteSpace: 'nowrap' }}>
                        {value}: {val} ({percent}%)
                      </span>
                    );
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="custom-no-data">No data found</div>
          )}
        </div>
      </div>
    );
  }

  // 2. Table-style List Report (Brief Dashboard Specialized)
  if (name.toLowerCase().includes("leads & followup") || name.toLowerCase().includes("task & appointment") || name.toLowerCase().includes("pipeline")) {
    const listData = Array.isArray(data) ? data : [];
    const isLeadFollowup = name.toLowerCase().includes("leads & followup");
    const isPipeline = name.toLowerCase().includes("pipeline");

    return (
      <div
        className="custom-list-body"
        onClick={(e) => {
          if (!isPipeline && onTileClick) {
            onTileClick(tile);
          }
        }}
        style={{ cursor: (!isPipeline && onTileClick) ? 'pointer' : 'default' }}
      >
        <span className="custom-widget-label">{tile.ReportName}</span>
        <div className="custom-table-scroll-container">
          <table className="custom-dashboard-table">
            <thead>
              <tr>
                {isPipeline ? (
                  <>
                    <th>User_Login</th>
                    <th>Pipeline</th>
                    <th style={{ textAlign: 'right' }}>New Added</th>
                  </>
                ) : isLeadFollowup ? (
                  <>
                    <th>USER ID</th>
                    <th>USER NAME</th>
                    <th style={{ textAlign: 'right' }}>NEW LEADS ADDED</th>
                  </>
                ) : (
                  <>
                    <th>UserByName</th>
                    <th>Executive</th>
                    <th style={{ textAlign: 'right' }}>TotalCreated</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {listData.length > 0 ? (
                listData.slice(0, 10).map((item, i) => (
                  <tr key={i}>
                    {isPipeline ? (
                      <>
                        <td className="truncate-cell" title={getSafeVal(item, ['UserByName', 'user_login'])}>{getSafeVal(item, ['UserByName', 'user_login'])}</td>
                        <td className="truncate-cell">{getSafeVal(item, ['Pipeline', 'PipelineName'], '---')}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#2563eb' }}>{getSafeVal(item, ['NewAdded', 'Value', 'count'], 0)}</td>
                      </>
                    ) : isLeadFollowup ? (
                      <>
                        <td>{getSafeVal(item, ['user_id', 'user_login', 'UserID', 'UserId', 'userid', 'id'])}</td>
                        <td className="truncate-cell">{getSafeVal(item, ['UserName', 'UserByName', 'Name', 'FullName'])}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#2563eb' }}>{getSafeVal(item, ['NewLeadsAdded', 'TotalCount', 'Value', 'count'], 0)}</td>
                      </>
                    ) : (
                      <>
                        <td className="truncate-cell">{getSafeVal(item, ['UserByName', 'UserName', 'Name', 'FullName'])}</td>
                        <td className="truncate-cell">{getSafeVal(item, ['ExecutiveName', 'Executive', 'UserName', 'Executive'])}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#2563eb' }}>{getSafeVal(item, ['TotalCreated', 'Total', 'TotalCount', 'Value', 'count'], 0)}</td>
                      </>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} style={{ textAlign: 'center', padding: '1.5rem', color: '#94a3b8' }}>No record(s) found.</td>
                </tr>
              )}
              {/* Total Row */}
              <tr className="total-row" style={{ borderTop: '2px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                <td style={{ fontWeight: 700 }}>Total</td>
                <td style={{ fontWeight: 700, textAlign: 'center' }}>###</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: '#2563eb' }}>
                  {isPipeline
                    ? listData.reduce((sum, item) => sum + parseInt(getSafeVal(item, ['NewAdded', 'Value'], 0) || 0), 0)
                    : isLeadFollowup
                      ? listData.reduce((sum, item) => sum + parseInt(getSafeVal(item, ['NewLeadsAdded', 'TotalCount', 'Value'], 0) || 0), 0)
                      : listData.reduce((sum, item) => sum + parseInt(getSafeVal(item, ['TotalCreated', 'Total', 'TotalCount', 'Value'], 0) || 0), 0)
                  }
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // 3. List Report (Top 10 etc.)
  if (tile.SectionID === 3 || name.toLowerCase().includes("top 10")) {
    const listData = Array.isArray(data) ? data : [];
    return (
      <div
        className="custom-list-body"
        onClick={() => onTileClick && onTileClick(tile)}
        style={{ cursor: onTileClick ? 'pointer' : 'default' }}
      >
        <label className="custom-widget-label">{name}</label>
        <div className="custom-list-items">
          {listData.length > 0 ? (
            listData.slice(0, 10).map((item, i) => {
              const rawPersonName = getSafeVal(item, ['PersonName', 'Name', 'UserName', 'UserByName', 'ExecutiveName', 'Executive', 'Source', 'PipelineName'], 'User');
              const personName = maskValue ? maskValue('PersonName', rawPersonName) : rawPersonName;
              const initials = getInitialsByString(rawPersonName);
              const bgColor = AVATAR_COLORS[i % AVATAR_COLORS.length];
              const randomTextColor = '#' + Math.floor(Math.abs(Math.sin(i + 1) * 16777215)).toString(16).padStart(6, '0');
              
              const subtext = getSafeVal(item, ['LeadNo', 'user_login', 'Id'], '');
              const displaySubtext = subtext && subtext !== '---' ? `ID: ${subtext}` : '';
              const score = parseFloat(getSafeVal(item, ['Value', 'CurrentScore', 'score', 'TotalCount', 'TotalCreated', 'NewAdded', 'LeadCount', 'count'], 0)).toFixed(2);

              return (
                <div
                  key={i}
                  className="custom-list-row"
                  onClick={(e) => {
                    if (onListItemClick) {
                      e.stopPropagation();
                      onListItemClick(item, tile);
                    }
                  }}
                  style={{ cursor: onListItemClick ? 'pointer' : 'default' }}
                >
                  <div className="custom-row-left">
                    <div className="custom-avatar-initials" style={{ backgroundColor: bgColor, color: randomTextColor }}>
                      {initials}
                    </div>
                    <div className="custom-info">
                      <span className="custom-name">{personName}</span>
                      {displaySubtext && <span className="custom-id">{displaySubtext}</span>}
                    </div>
                  </div>
                  <span className="custom-score">{score}</span>
                </div>
              );
            })
          ) : (
            <div className="custom-no-data">No report data found</div>
          )}
        </div>
      </div>
    );
  }

  // 5. QA Dashboard Datewise View
  if (name.toLowerCase().includes("datewise view") || sectionName === "enquiries") {
    const listData = Array.isArray(data) ? data : [];

    const totalNew = listData.reduce((acc, curr) => acc + parseInt(curr.NewEnquiries || 0), 0);
    const totalConv = listData.reduce((acc, curr) => acc + parseInt(curr.EnquiriesConvertedToLeads || curr.EnquiriesConverted || 0), 0);

    return (
      <div
        className="custom-list-body"
        style={{ display: 'flex', flexDirection: 'column', cursor: onTileClick ? 'pointer' : 'default' }}
        onClick={() => onTileClick && onTileClick(tile)}
      >
        <label className="custom-widget-label">{name || 'Datewise View()'}</label>
        <div className="custom-table-container" style={{ flex: 1, overflow: 'auto', padding: '0 8px' }}>
          {listData.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ textAlign: 'left', padding: '8px', color: '#64748b', fontWeight: '500' }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '8px', color: '#64748b', fontWeight: '500' }}>AgentName</th>
                  <th style={{ textAlign: 'center', padding: '8px', color: '#64748b', fontWeight: '500' }}>NewEnquiries</th>
                  <th style={{ textAlign: 'center', padding: '8px', color: '#64748b', fontWeight: '500' }}>EnquiriesConverted</th>
                </tr>
              </thead>
              <tbody>
                {listData.map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px', color: '#334155' }}>{item.Date}</td>
                    <td style={{ padding: '8px', color: '#334155' }}>{maskValue ? maskValue('AgentName', item.AgentName) : item.AgentName}</td>
                    <td style={{ padding: '8px', textAlign: 'center', color: '#0ea5e9', fontWeight: 600 }}>{item.NewEnquiries}</td>
                    <td style={{ padding: '8px', textAlign: 'center', color: '#0ea5e9', fontWeight: 600 }}>{item.EnquiriesConvertedToLeads || item.EnquiriesConverted || 0}</td>
                  </tr>
                ))}
                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '8px', fontWeight: 'bold', color: '#334155' }}>Total</td>
                  <td style={{ padding: '8px', textAlign: 'center', fontWeight: 'bold', color: '#334155' }}>###</td>
                  <td style={{ padding: '8px', textAlign: 'center', color: '#0ea5e9', fontWeight: 'bold' }}>{totalNew}</td>
                  <td style={{ padding: '8px', textAlign: 'center', color: '#0ea5e9', fontWeight: 'bold' }}>{totalConv}</td>
                </tr>
              </tbody>
            </table>
          ) : (
            <div className="custom-no-data">No report data found</div>
          )}
        </div>
      </div>
    );
  }


  // 3. Counter (Default)
  return (
    <div className="custom-counter-body" onClick={() => onTileClick && onTileClick(tile)} style={{ cursor: onTileClick ? 'pointer' : 'default' }}>
      <div className="custom-counter-value">{value}</div>
      <div className="custom-counter-label">{name}</div>
    </div>
  );
};

const SALES_DASHBOARD_LIST = [
  "Enquiries", "Leads", "Lead", "Merge Duplicate", "Follow-ups", "Conversions",
  "Segmentation", "Call List", "Tasks", "Appointments", "Physical Appointments",
  "Lead Activities", "Custom Events", "Create Event", "Webhook Events",
  "Pipeline Deal", "Pipeline History", "Pipeline", "Quotations", "Invoices", "Revenue",
  "Credit Notes", "Customer Ledger"
];

const Dashboard = () => {
  const navigate = useNavigate();
  const { width, containerRef } = useContainerWidth();
  const [activeTab, setActiveTab] = useState('');
  const [tabs, setTabs] = useState([]);
  const [tabMetadata, setTabMetadata] = useState([]); // Full dashboard objects
  const [customDashboardTiles, setCustomDashboardTiles] = useState([]);
  const [customDashboardData, setCustomDashboardData] = useState({}); // Widget data by SectionName
  const [showCustomDashboard, setShowCustomDashboard] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingTiles, setLoadingTiles] = useState(false);
  const [sourcePieData, setSourcePieData] = useState({}); // Data for source pie charts
  const [layoutInitialized, setLayoutInitialized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const { hierarchyList } = useHierarchy();
  const { permissions } = usePermissions('MOD10176');
  const { maskValue, maskObject, isHiddenField } = useFieldMasking('MOD10176');

  const defaultUserId = localStorage.getItem('ustrUserId') || localStorage.getItem('userId') || localStorage.getItem('UserId') || '34594';
  const [selectedUserId, setSelectedUserId] = useState(defaultUserId);

  const canEdit = permissions.length > 0 ? (permissions[0]?.Edit !== false) : true;
  const canAdd = permissions.length > 0 ? (permissions[0]?.Add !== false) : true;
  const canDelete = permissions.length > 0 ? (permissions[0]?.Delete !== false) : true;

  const TOKEN = API_ENDPOINTS.TOKE.UNIVERSAL_TOKEN;
  const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
  const NEW_BASE_URL = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
  const API_BASE = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
  const Get_Custom_Dashboard_List = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.GET_CUSTOME_DASHBOARD_LIST}`;
  const Get_Task_Details = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.GET_SCORECARD_DETAILS_TASK}`;
  const Get_Appointment_Details = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.GET_SCORECARD_DETAILS_APPOINTMENT}`;
  const Get_FollowUp_Details = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.GET_SCORECARD_DETAILS_FollowUp}`;
  const Get_Pipeline_Details = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.GET_SCORECARD_DETAILS_Pipeline}`;
  const Get_saveTiles = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.GET_SAVE_TILES}`;
  const Save_Tiles = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.SAVE_TILES}`;
  const Get_DashBoard_Saved_Tiles = `${NEWV3_BASE_URL}${API_ENDPOINTS.DASHBOARD.GET_DASHBOAD_TILES}`;
  const Get_List_Report_DashBoard = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.GET_LIST_REPORT_DASHBOARD}`;
  const Get_Source_Report_DashBoard = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.GET_SOURCE_DETAILS}`;
  const Get_LeadAndFollowup_LIST_Report_DashBoard = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.GetLeadReportsForLeadAndFollowup_List}`;
  const GET_REPORT_FOR_TASK_AND_APPOINTMENT = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.GET_REPORT_FOR_TASK_AND_APPOINTMENT}`;
  const Get_Enquiry_Details = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.GET_ENQUIRY_DETAILS}`;
  const Get_Lead_Details = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.GET_LEAD_DETAILS}`;
  const SAVE_DASHBOARD_ORDER = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.DASHBOARD_ORDER}`;
  const Get_ScrapBook = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.GET_SCRAPBOOK}`;
  const Get_Calender = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.GET_CALENDER_VIEW}`;
  const Save_Notes = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.SAVE_SCRAP_NOTES}`;
  const Get_Convsersion_Details = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.GET_CONVERSION_DETAILS}`;
  const Get_Qa_DashBoard_Data = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.QA_DASHBOARD_TILES}`;
  const GET_PIPELINE_REPORTS = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.PIPEPINE_REPORTS}`;
  const formatDateForBrief = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [taskData, setTaskData] = useState({
    overdue: 0,
    dueToday: 0,
    dueIn7Days: 0,
    dueInThisMonth: 0,
    executives: []
  });
  const [loadingTasks, setLoadingTasks] = useState(false);

  const [appointmentData, setAppointmentData] = useState({
    overdue: 0,
    dueToday: 0,
    dueIn7Days: 0,
    dueInThisMonth: 0,
    executives: []
  });
  const [loadingAppointments, setLoadingAppointments] = useState(false);

  const [followUpData, setFollowUpData] = useState({
    overdue: 0,
    dueToday: 0,
    dueIn7Days: 0,
    dueInThisMonth: 0,
    executives: []
  });
  const [loadingFollowUps, setLoadingFollowUps] = useState(false);
  const [pipelineData, setPipelineData] = useState({
    stageWise: [],
    executiveWise: [],
    expiringDeals: [],
    estimatedClosures: []
  });
  const [loadingPipeline, setLoadingPipeline] = useState(false);

  const [enquiryData, setEnquiryData] = useState({
    today: 0,
    thisMonth: 0,
    stillOpen: 0,
    sourceData: []
  });
  const [loadingEnquiry, setLoadingEnquiry] = useState(false);

  const [leadData, setLeadData] = useState({
    counters: {
      createdAssignedToday: 0,
      assignedToday: 0,
      assignedTillToday: 0,
      createdToday: 0,
      createdThisMonth: 0,
      notFollowedUpYet: 0
    },
    topLeads: [],
    sourceData: []
  });
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [conversionData, setConversionData] = useState(null);
  const [loadingConversion, setLoadingConversion] = useState(false);

  // Tab management states
  const [showSortPanel, setShowSortPanel] = useState(false);
  const [sortedTabMetadata, setSortedTabMetadata] = useState([]);
  const [showTodoPanel, setShowTodoPanel] = useState(false);
  const [activeTodoTab, setActiveTodoTab] = useState('todo');
  const [todoDate, setTodoDate] = useState(new Date());
  const [showScrapbookAddMenu, setShowScrapbookAddMenu] = useState(false);
  const [scrapbookItems, setScrapbookItems] = useState([]);
  const [loadingScrapbook, setLoadingScrapbook] = useState(false);

  const [calendarData, setCalendarData] = useState(null);
  const [loadingCalendar, setLoadingCalendar] = useState(false);

  // Modal states
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [showAddChecklistModal, setShowAddChecklistModal] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);

  // Form states
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [noteTheme, setNoteTheme] = useState('#e8b01c-0-0-0');

  const [checklistTitle, setChecklistTitle] = useState('');
  const [checklistNewItem, setChecklistNewItem] = useState('');
  const [checklistTheme, setChecklistTheme] = useState('#d9e8f0');

  const formatTodoDate = (date) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const d = date.getDate().toString().padStart(2, '0');
    const m = months[date.getMonth()];
    const y = date.getFullYear();
    return `${d}-${m}-${y}`;
  };

  const handlePrevDate = () => {
    const newDate = new Date(todoDate);
    newDate.setDate(newDate.getDate() - 1);
    setTodoDate(newDate);
  };

  const handleNextDate = () => {
    const newDate = new Date(todoDate);
    newDate.setDate(newDate.getDate() + 1);
    setTodoDate(newDate);
  };

  const [activePieIndex, setActivePieIndex] = useState(null);

  const onPieEnter = (_, index) => {
    setActivePieIndex(index);
  };

  const onPieLeave = () => {
    setActivePieIndex(null);
  };

  const RADIAN = Math.PI / 180;
  const renderLeadPieLabel = ({ cx, cy, midAngle, outerRadius, name, percent, fill, index }) => {
    // For extremely small partitions, drastically increase line lengths alternately to prevent overlap
    let staggerOffset = (index % 3) * 20;
    let horizontalLen = 15 + (index % 3) * 25;

    if (percent < 0.05) {
      // Push small slice headings far from each other by using a wider stagger
      staggerOffset = (index % 5) * 40;
      horizontalLen = 20 + (index % 4) * 45;
    }

    const MID_RADIUS = outerRadius + 20 + staggerOffset;

    // Only use artificial angle for EXACTLY overlapping zero slices (kmukesh fix)
    // Normal small slices MUST use their natural angle so they stay in front of their partition
    let labelAngle = midAngle;
    if (percent === 0 && index > 0) {
      const sign = index % 2 === 0 ? 1 : -1;
      const step = Math.floor((index + 1) / 2);
      labelAngle = 90 + sign * (step * 20);
    }

    const sx = cx + outerRadius * Math.cos(-midAngle * RADIAN);
    const sy = cy + outerRadius * Math.sin(-midAngle * RADIAN);
    const mx = cx + MID_RADIUS * Math.cos(-labelAngle * RADIAN);
    const my = cy + MID_RADIUS * Math.sin(-labelAngle * RADIAN);

    // Recharts horizontal elbow logic
    const isRight = Math.cos(-labelAngle * RADIAN) >= 0;

    const ex = mx + (isRight ? horizontalLen : -horizontalLen);
    const ey = my;

    const textX = ex + (isRight ? 6 : -6);
    const textAnchor = isRight ? 'start' : 'end';

    // Nudge text vertically to prevent bounding box overlap without changing the line's natural position
    const textDy = ((index % 3) - 1) * 12; // -12, 0, 12 pixels

    return (
      <g>
        <path d={`M${sx},${sy} L${mx},${my} L${ex},${ey}`} stroke={fill} strokeWidth={1} fill="none" />
        <circle cx={ex} cy={ey} r={2} fill={fill} />
        <text
          x={textX}
          y={ey}
          dy={textDy}
          textAnchor={textAnchor}
          dominantBaseline="central"
          fill="#333"
          fontSize={11}
          fontFamily="'Inter', sans-serif"
        >
          {`${name}: ${(percent * 100).toFixed(1)} %`}
        </text>
      </g>
    );
  };

  const renderActivePieShape = (props) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 12}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 15}
          outerRadius={outerRadius + 18}
          fill={fill}
        />
      </g>
    );
  };

  const [savingOrder, setSavingOrder] = useState(false);
  const [showMailSettings, setShowMailSettings] = useState(false);
  const [showMailReport, setShowMailReport] = useState(false);
  const [mailEditData, setMailEditData] = useState(null);

  // Drill-down states
  const [drillDownTasks, setDrillDownTasks] = useState([]);
  const [drillDownLoading, setDrillDownLoading] = useState(false);
  const [drillDownFilter, setDrillDownFilter] = useState(null); // { label: 'Overdue', filterText: '...' }

  useEffect(() => {
    fetchDynamicTabs();
  }, [selectedUserId]);

  const fetchedTabsRef = useRef({});

  useEffect(() => {
    if (!activeTab) return;

    if (fetchedTabsRef.current.userId !== selectedUserId) {
      fetchedTabsRef.current = { userId: selectedUserId };
    }

    if (fetchedTabsRef.current[activeTab]) {
      return;
    }

    let handled = false;

    if (activeTab === 'Tasks') {
      fetchTaskData();
      handled = true;
    } else if (activeTab === 'Appointments') {
      fetchAppointmentData();
      handled = true;
    } else if (activeTab === 'Follow-ups') {
      fetchFollowUpData();
      handled = true;
    } else if (activeTab === 'Pipeline') {
      fetchPipelineData();
      handled = true;
    } else if (activeTab === 'Conversions') {
      fetchConversionData();
      handled = true;
    } else if (activeTab.toLowerCase().includes('enquir')) {
      fetchEnquiryData();
      handled = true;
    } else if (activeTab.toLowerCase().includes('lead')) {
      fetchLeadData();
      handled = true;
    } else {
      // Find matching metadata for custom lab
      const meta = tabMetadata.find(t => {
        const dName = (t.CustomDashboard || t.DashboardName || "").trim();
        return dName === (activeTab || "").trim();
      });

      if (meta) {
        const dashId = meta.DashBoardId || meta.CustomDashboardId || meta.Id || meta.id || meta.DashboardId;
        if (dashId) {
          fetchCustomDashboardTiles(dashId);
          handled = true;
        }
      }
    }

    if (handled) {
      fetchedTabsRef.current[activeTab] = true;
    }
  }, [activeTab, tabMetadata, selectedUserId]);

  const fetchCustomDashboardTiles = async (dashId) => {
    setLoadingTiles(true);
    setLoading(true);
    try {
      const ustrUserId = selectedUserId;
      const payload = {
        Status: "",
        Message: "",
        Token: String(TOKEN),
        Details: {
          UserId: parseInt(ustrUserId, 10),
          CustomDashboardId: parseInt(dashId, 10),
          IsFromManage: 0
        }
      };
      const response = await axios.post(Get_DashBoard_Saved_Tiles, payload);
      if (response.data && response.data.Details) {
        let tiles = Array.isArray(response.data.Details) ? response.data.Details : [];

        // Filter out empty tiles (ReportName is empty and SectionID is 0)
        tiles = tiles.filter(tile => tile.ReportName || tile.SectionID !== 0);

        setCustomDashboardTiles(tiles);

        // Fetch data for each tile based on its type/name
        tiles.forEach(tile => {
          const name = (tile.ReportName || '').trim().toLowerCase();
          const sectionName = tile.SectionName || '';
          const isBriefReport = name.includes("leads & followup") ||
            name.includes("task & appointment") ||
            name.includes("pipeline") ||
            name.includes("lead reports");

          // Cache check: Don't re-fetch if we already have data for this widget
          const existingData = customDashboardData[tile.ReportName] || customDashboardData[sectionName];
          if (Array.isArray(existingData) && existingData.length > 0) {
            return;
          }

          if (name.includes("source wise") || name.includes("sourcewise") || name.includes("distribution")) {
            if (sectionName) {
              fetchSourcePieData(sectionName, (tile.ReportName || '').trim());
            }
          } else if (isBriefReport) {
            fetchBriefListData(tile, dashId);
          } else if (name.includes("datewise view") || sectionName === "enquiries") {
            if (sectionName) {
              fetchQaDashboardData(sectionName, (tile.ReportName || '').trim());
            }
          } else if ((tile.SectionID === 3 || name.includes("top 10")) && !isBriefReport) {
            if (sectionName) {
              fetchWidgetData(sectionName, (tile.ReportName || '').trim());
            }
          }
        });
      }
    } catch (error) {
      console.error('Error fetching custom dashboard tiles:', error);
    } finally {
      setLoadingTiles(false);
      setLoading(false);
    }
  };

  const fetchSourcePieData = async (sectionName, reportName) => {
    try {
      const ustrUserId = selectedUserId;
      let actualSection = sectionName;
      if ((reportName || '').toLowerCase().includes('source')) {
        actualSection = 'leads';
      }

      const payload = {
        Status: "",
        Message: "",
        Token: String(TOKEN),
        Details: {
          UserId: ustrUserId,
          strLeadSection: actualSection
        }
      };
      const response = await axios.post(Get_Source_Report_DashBoard, payload);
      if (response.data && response.data.Details) {
        // Expected data format for Recharts Pie: [{ name: '...', value: ... }]
        // We'll trust the API returns compatible format or map it
        const rawData = response.data.Details;
        const mappedData = Array.isArray(rawData) ? rawData.map(item => ({
          name: item.Source || item.Name || item.label || 'Other',
          value: parseFloat(item.LeadCount || item.TotalCount || item.Value || item.count || 0)
        })) : [];

        setSourcePieData(prev => ({
          ...prev,
          [reportName]: mappedData,
          ...(sectionName ? { [sectionName]: mappedData } : {})
        }));
      }
    } catch (error) {
      console.error(`Error fetching source pie data for ${sectionName}:`, error);
    }
  };

  const fetchWidgetData = async (sectionName, reportName) => {
    try {
      const ustrUserId = selectedUserId;
      let actualSection = sectionName;
      if ((reportName || '').toLowerCase().includes('top 10')) {
        actualSection = 'leads';
      }

      const payload = {
        Status: "",
        Message: "",
        Token: String(TOKEN),
        Details: {
          UserId: ustrUserId,
          strLeadSection: actualSection
        }
      };
      const response = await axios.post(Get_List_Report_DashBoard, payload);
      if (response.data && response.data.Details) {
        setCustomDashboardData(prev => {
          // If this section already has data (e.g. from fetchBriefListData), don't overwrite with empty results
          const existingData = prev[reportName] || prev[sectionName];
          const newData = response.data.Details;
          if (Array.isArray(existingData) && existingData.length > 0 && Array.isArray(newData) && newData.length === 0) {
            return prev;
          }
          return {
            ...prev,
            [reportName]: newData,
            ...(sectionName ? { [sectionName]: newData } : {})
          };
        });
      }
    } catch (error) {
      console.error(`Error fetching widget data for ${sectionName}:`, error);
    }
  };

  const fetchQaDashboardData = async (sectionName, reportName) => {
    try {
      const ustrUserId = selectedUserId;
      const todayStr = formatDateForBrief(new Date());

      const payload = {
        Token: "-2295521862261168",
        Details: {
          UserID: String(ustrUserId),
          ParentID: String(ustrUserId),
          FromDate: todayStr,
          ToDate: todayStr,
          flagsDivs: false,
          CriteriaName: "0",
          Mode: "",
          TimeZone: "Asia/Kolkata"
        }
      };

      const response = await axios.post(Get_Qa_DashBoard_Data, payload);
      let responseData = response.data.Details || response.data.d || response.data;
      if (responseData) {
        const raw = responseData;
        const matrix = raw.lstDatewiseEnquiryMatrix_ER || raw || [];
        setCustomDashboardData(prev => ({
          ...prev,
          [reportName]: matrix,
          ...(sectionName ? { [sectionName]: matrix } : {})
        }));
      }
    } catch (error) {
      console.error(`Error fetching QA dashboard data for ${sectionName}:`, error);
    }
  };

  const fetchBriefListData = async (tile, dashId) => {
    const reportName = (tile.ReportName || '').trim();
    try {
      const ustrUserId = selectedUserId;

      const now = new Date();
      const dateTo = new Date();
      dateTo.setDate(now.getDate() - 1);
      const dateFrom = new Date();
      dateFrom.setDate(now.getDate() - 2);

      const todayStr = formatDateForBrief(dateTo);
      const yesterdayStr = formatDateForBrief(dateFrom);

      let endpoint = '';
      let payload = {};

      if (reportName.toLowerCase().includes("leads & followup")) {
        endpoint = Get_LeadAndFollowup_LIST_Report_DashBoard;
        payload = {
          Status: "",
          Message: "",
          Token: String(TOKEN),
          Details: {
            UserID: ustrUserId,
            ParentID: ustrUserId,
            FromDate: yesterdayStr,
            ToDate: todayStr,
            CriteriaName: "",
            TimeZone: "India Standard Time"
          }
        };
      } else if (reportName.toLowerCase().includes("task & appointment")) {
        endpoint = GET_REPORT_FOR_TASK_AND_APPOINTMENT;
        payload = {
          Token: String(TOKEN),
          Details: {
            UserID: parseInt(ustrUserId, 10),
            ParentID: parseInt(ustrUserId, 10),
            CriteriaName: "",
            FromDate: yesterdayStr,
            ToDate: todayStr,
            TimeZone: "India Standard Time"
          }
        };
      } else if (reportName.toLowerCase().includes("pipeline")) {
        endpoint = GET_PIPELINE_REPORTS;
        payload = {
          Token: "-2295521862261168",
          Details: {
            UserId: parseInt(ustrUserId, 10),
            strPipelineSection: "YourPipelineSectionIdentifier"
          }
        };
      }

      if (endpoint) {
        const response = await axios.post(endpoint, payload);
        let responseData = response.data.d || response.data;

        let details = null;
        if (responseData && responseData.Details) {
          details = responseData.Details;
        } else if (responseData) {
          details = responseData;
        }

        if (details) {
          // Robust parsing for stringified Details
          if (typeof details === 'string') {
            try {
              details = JSON.parse(details);
            } catch (e) {
              console.error('Error parsing Details string:', e);
            }
          }

          let targetList = [];
          if (Array.isArray(details)) {
            targetList = details;
          } else if (typeof details === 'object' && details !== null) {
            // 1. Explicit priority keys based on report type
            if (reportName.toLowerCase().includes("leads & followup")) {
              targetList = details.lstLeadReport_Lead_DayWise || details.lsLeadReport_Followup_DayWise || details.lstLeadReport_Lead || [];
            } else if (reportName.toLowerCase().includes("task & appointment")) {
              targetList = details.lstTask_LRDayWise || details.lstAppointment_LRDayWise || details.lstTask || [];
            } else if (reportName.toLowerCase().includes("pipeline")) {
              // The API might not have a Details wrapper, so we check if details itself has the list
              targetList = details.lstPipelineWisePipelineDetails_LRDayWise || details.lstUserWisePipelineDetails_LRDayWise || (responseData && responseData.lstPipelineWisePipelineDetails_LRDayWise) || [];
            }

            // 2. Fallback: Search for any key that looks like a list
            if (!targetList || targetList.length === 0) {
              const listKey = Object.keys(details).find(k =>
                k.toLowerCase().includes('daywise') ||
                k.toLowerCase().includes('list') ||
                k.toLowerCase().includes('table')
              );
              if (listKey) targetList = details[listKey];
            }

            // 3. Last fallback: First array found in object
            if (!targetList || !Array.isArray(targetList)) {
              targetList = Object.values(details).find(val => Array.isArray(val)) || [];
            }
          }

          setCustomDashboardData(prev => {
            // Protection: Don't overwrite existing valid data with empty/invalid data
            const key1 = reportName;
            const key2 = tile.SectionName;
            const existingData = (key1 && prev[key1] && prev[key1].length > 0) ? prev[key1] : prev[key2];

            if (Array.isArray(existingData) && existingData.length > 0 && (!targetList || targetList.length === 0)) {
              return prev;
            }

            return {
              ...prev,
              [reportName]: targetList,
              ...(tile.SectionName ? { [tile.SectionName]: targetList } : {})
            };
          });
        }
      }
    } catch (error) {
      console.error(`Error fetching brief list data for ${reportName}:`, error);
    }
  };

  const fetchDynamicTabs = async () => {
    setLoading(true);
    try {
      const ustrUserId = selectedUserId;
      const payload = {
        Status: "",
        Message: "",
        Token: String(TOKEN),
        Details: { UserId: ustrUserId, ManageDash: 0 }
      };

      const response = await axios.post(Get_Custom_Dashboard_List, payload);
      if (response.data && response.data.Details) {
        let rawTabs = Array.isArray(response.data.Details) ? response.data.Details : [];

        // Filter tabs to show only Sales Module dashboards or user-created ones
        rawTabs = rawTabs.filter(t => {
          const dashName = (t.CustomDashboard || t.DashboardName || "").trim();
          const isPredefined = t.IsPredifine === true || t.IsPredifine === 1;

          const isInSalesList = SALES_DASHBOARD_LIST.some(item =>
            item.toLowerCase().trim() === dashName.toLowerCase()
          );

          // Rule: Show if it's in the Sales list OR if it's NOT predefined (user created)
          return isInSalesList || !isPredefined;
        });

        setTabMetadata(rawTabs);
        setSortedTabMetadata([...rawTabs].sort((a, b) => (a.CustomDashboardOrder || 0) - (b.CustomDashboardOrder || 0)));

        const mappedTabs = rawTabs
          .filter(t => t.IsVisible !== 0) // Only show visible tabs in main nav
          .map(t => t.CustomDashboard || t.DashboardName)
          .filter(Boolean);
        setTabs(mappedTabs);

        if (mappedTabs.length > 0 && !activeTab) {
          setActiveTab(mappedTabs[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching dynamic tabs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchScrapbookItems = async () => {
    setLoadingScrapbook(true);
    try {
      const ustrUserId = selectedUserId;
      const payload = {
        Token: String(TOKEN),
        LoggedUserId: ustrUserId,
        Message: "",
        MAC_Address: "",
        IP_Address: "",
        Details: {
          UserID: ustrUserId,
          pageNo: "1",
          RecordsPerPage: 100,
          SearchNoteText: ""
        }
      };
      const response = await axios.post(Get_ScrapBook, payload);
      if (response.data && response.data.Details) {
        setScrapbookItems(Array.isArray(response.data.Details) ? response.data.Details : []);
      }
    } catch (error) {
      console.error('Error fetching Scrapbook items:', error);
    } finally {
      setLoadingScrapbook(false);
    }
  };

  const fetchCalendarData = async () => {
    setLoadingCalendar(true);
    try {
      const ustrUserId = selectedUserId;
      const payload = {
        Status: 1,
        Message: "",
        Token: String(TOKEN),
        Details: {
          UserId: ustrUserId,
          ActivityDate: formatTodoDate(todoDate)
        }
      };
      const response = await axios.post(Get_Calender, payload);
      if (response.data && response.data.Details) {
        setCalendarData(response.data.Details);
      }
    } catch (error) {
      console.error('Error fetching Calendar data:', error);
    } finally {
      setLoadingCalendar(false);
    }
  };

  const isImageUrl = (url) => {
    if (!url) return false;
    const cleanUrl = url.split('?')[0].toLowerCase();
    return cleanUrl.endsWith('.png') || cleanUrl.endsWith('.jpg') || cleanUrl.endsWith('.jpeg') || cleanUrl.endsWith('.gif') || cleanUrl.endsWith('.webp') || cleanUrl.endsWith('.svg');
  };

  const handleSelectMediaForScrapbook = async (media) => {
    try {
      const ustrUserId = selectedUserId;
      const payload = {
        Token: String(TOKEN),
        LoggedUserId: ustrUserId,
        Message: "",
        MAC_Address: "",
        IP_Address: "",
        Details: {
          Title: "",
          ScrapBookId: 0,
          ItemType: "Attachment",
          Note: media.url || media.thumbnail || "",
          Theme: "white",
          Action: "InsertScrapBook",
          Active: true,
          Userid: String(ustrUserId)
        }
      };

      const response = await axios.post(Save_Notes, payload);
      if (response.data && (response.data.Status === 1 || response.data.Message === "Success" || response.data.Message === "Record Saved successfully")) {
        fetchScrapbookItems();
      }
    } catch (error) {
      console.error("Error saving media to scrapbook:", error);
    }
  };

  const handleSaveScrapbookItem = async (itemType) => {
    try {
      const ustrUserId = selectedUserId;
      const title = itemType === 'Notes' ? noteTitle : checklistTitle;
      const theme = itemType === 'Notes' ? noteTheme : checklistTheme;
      const note = itemType === 'Notes' ? noteContent : checklistNewItem;

      const payload = {
        Token: String(TOKEN),
        LoggedUserId: ustrUserId,
        Message: "",
        MAC_Address: "",
        IP_Address: "",
        Details: {
          Title: title || "",
          ScrapBookId: 0,
          ItemType: itemType,
          Note: itemType === 'Notes' ? note : "",
          Theme: theme,
          Action: "InsertScrapBook",
          Active: true,
          Userid: String(ustrUserId),
          CheckList: itemType === 'CheckList' && checklistNewItem.trim() ? [
            {
              CheckListId: 0,
              ScrapBookId: 0,
              Content: checklistNewItem,
              Status: 0,
              Action: "InsertScrapBook"
            }
          ] : []
        }
      };

      const response = await axios.post(Save_Notes, payload);
      if (response.data && (response.data.Status === 1 || response.data.Message === "Success" || response.data.Message === "Record Saved successfully")) {
        if (itemType === 'Notes') {
          setShowAddNoteModal(false);
          setNoteTitle('');
          setNoteContent('');
        } else {
          setShowAddChecklistModal(false);
          setChecklistTitle('');
          setChecklistNewItem('');
        }
        fetchScrapbookItems();
      }
    } catch (error) {
      console.error(`Error saving ${itemType}:`, error);
    }
  };

  useEffect(() => {
    if (showTodoPanel) {
      if (activeTodoTab === 'todo') {
        fetchCalendarData();
      } else if (activeTodoTab === 'scrapbook') {
        fetchScrapbookItems();
      }
    }
  }, [showTodoPanel, activeTodoTab, todoDate, selectedUserId]);

  const fetchConversionData = async () => {
    setLoadingConversion(true);
    try {
      const ustrUserId = selectedUserId;
      const payload = {
        Status: "",
        Message: "",
        Token: String(TOKEN),
        Details: {
          UserId: String(ustrUserId),
          strConversionSection: "conversions"
        }
      };

      const response = await axios.post(Get_Convsersion_Details, payload);
      if (response.data && response.data.Details) {
        setConversionData(response.data.Details);
      }
    } catch (error) {
      console.error('Error fetching Conversion data:', error);
    } finally {
      setLoadingConversion(false);
    }
  };

  const handleLeadNavigation = (filter, forceNotFollowUp = 0) => {
    let payloadState = { isRedirect: true, notFollowUp: forceNotFollowUp, filterText: "" };
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const formatDt = (dt) => `${String(dt.getDate()).padStart(2, '0')}-${months[dt.getMonth()]}-${dt.getFullYear()}`;

    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const todayLiteral = `'${formatDt(today)}'`;
    const tomorrowLiteral = `'${formatDt(tomorrow)}'`;

    if (filter === 'CreatedAndAssignedToday') {
      payloadState.filterText = `( CAST(LastFollowupedOn AS DATE) BETWEEN ${todayLiteral} AND ${tomorrowLiteral} )`;
    } else if (filter === 'AssignedToday') {
      payloadState.filterText = `( CAST(LastFollowupedOn AS DATE) BETWEEN ${todayLiteral} AND ${tomorrowLiteral} )`;
    } else if (filter === 'AssignedTillToday') {
      payloadState.filterText = "";
    } else if (filter === 'CreatedToday') {
      payloadState.filterText = `( CAST(CreatedOn1 AS DATE) BETWEEN ${todayLiteral} AND ${tomorrowLiteral} )`;
    } else if (filter === 'CreatedThisMonth') {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const firstDayLiteral = `'${formatDt(firstDay)}'`;
      payloadState.filterText = `( CAST(CreatedOn1 AS DATE) BETWEEN ${firstDayLiteral} AND ${tomorrowLiteral} )`;
    } else if (filter.toLowerCase().includes('notfollowed') || filter.toLowerCase().includes('notfollowup')) {
      payloadState.notFollowUp = 1;
      payloadState.filterText = "";
    } else if (filter.startsWith('ID=') || filter.startsWith('ID =') || filter.startsWith('LeadNo')) {
      payloadState.filterText = filter;
    }

    navigate('/leads', { state: payloadState });
  };

  const fetchTaskData = async () => {
    setLoadingTasks(true);
    try {
      const ustrUserId = selectedUserId;
      const payload = {
        Details: {
          UserId: ustrUserId,
          strTaskSection: "tasks"
        },
        Message: "",
        Status: "",
        Token: TOKEN
      };

      const response = await axios.post(Get_Task_Details, payload);
      // Map response matching the provided JSON structure
      if (response.data && response.data.Message === 'Success' && response.data.Details) {
        const details = response.data.Details;
        const matrixList = details.TaskMatrix_CLS_List || [];
        const executivesList = details.TaskExecutive_CLS_List || [];

        const metrics = matrixList.length > 0 ? matrixList[0] : {};

        // map executive list matching the ui mapping
        const mappedExecutives = executivesList.map(exec => ({
          name: exec.ExecutiveName || '',
          userId: exec.Executive || '',
          count: parseInt(exec.TasksDue) || 0,
          avatar: exec.Image || ''
        }));

        setTaskData({
          overdue: parseInt(metrics.Overdue) || 0,
          dueToday: parseInt(metrics.DueToday) || 0,
          dueIn7Days: parseInt(metrics.DueIn7Days) || 0,
          dueInThisMonth: parseInt(metrics.DueThisMonth) || 0,
          executives: mappedExecutives
        });
      }
    } catch (error) {
      console.error('Error fetching task details:', error);
    } finally {
      setLoadingTasks(false);
    }
  };

  const fetchAppointmentData = async () => {
    setLoadingAppointments(true);
    try {
      const ustrUserId = selectedUserId;
      const payload = {
        Details: {
          UserId: ustrUserId,
          strAppointmentSection: "appointments"
        },
        Message: "",
        Status: "",
        Token: TOKEN
      };
      const response = await axios.post(Get_Appointment_Details, payload);
      if (response.data && response.data.Message === 'Success' && response.data.Details) {
        const details = response.data.Details;
        const matrixList = details.AppointmentMatrix_CLS_List || [];
        const executivesList = details.AppointmentExecutive_CLS_List || [];
        const metrics = matrixList.length > 0 ? matrixList[0] : {};
        const mappedExecutives = executivesList.map(exec => ({
          name: exec.ExecutiveName || '',
          userId: (exec.Executive || '').split(',')[0],
          fullUserId: exec.Executive || '',
          count: parseInt(exec.AppointmentsDue) || 0,
          avatar: exec.Image || ''
        }));
        setAppointmentData({
          overdue: parseInt(metrics.Overdue) || 0,
          dueToday: parseInt(metrics.DueToday) || 0,
          dueIn7Days: parseInt(metrics.DueIn7Days) || 0,
          dueInThisMonth: parseInt(metrics.DueThisMonth) || 0,
          executives: mappedExecutives
        });
      }
    } catch (error) {
      console.error('Error fetching appointment details:', error);
    } finally {
      setLoadingAppointments(false);
    }
  };

  const fetchFollowUpData = async () => {
    setLoadingFollowUps(true);
    console.log('Fetching FollowUp Data...');
    try {
      const ustrUserId = selectedUserId;
      const payload = {
        Details: {
          UserId: ustrUserId,
          strFollowupSection: "followups"
        },
        Message: "",
        Status: "",
        Token: TOKEN
      };
      const response = await axios.post(Get_FollowUp_Details, payload);
      console.log('FollowUp API RAW response:', response.data);

      if (!response.data) {
        console.warn('FollowUp API: No data in response');
        return;
      }

      // Handle .d wrapper if present (common in ASP.NET)
      let data = response.data.d || response.data;

      // Handle JSON stringified data if necessary
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error('FollowUp API: Failed to parse string data', e);
        }
      }

      // Try Details wrapper first (same pattern as Tasks/Appointments)
      const details = data.Details || data;
      console.log('FollowUp API Details:', details);

      // Executive list — try all variations including known typos (CL5 vs CLS)
      const rawExecs =
        details.FollowupDueExecutive_CLS_List ||
        details.FollowupDueExecutive_CL5_List ||
        details.FollowupExecutive_CLS_List ||
        details.FollowupExecutive_Cls_List ||
        details.ExecutiveList ||
        (Array.isArray(details) ? details : []);

      console.log('FollowUp API rawExecs:', rawExecs);

      // Metric matrix — try all variations including known typos (CL5 vs CLS)
      const matrixList =
        details.FollowupDueMatrix_CLS_List ||
        details.FollowupDueMatrix_CL5_List ||
        details.FollowupMatrix_CLS_List ||
        details.FollowupMatrix_Cls_List ||
        details.MatrixList ||
        [];
      const metrics = matrixList.length > 0 ? matrixList[0] : {};

      const mappedExecutives = rawExecs.map(exec => {
        // Log one item to see keys
        return {
          name: exec.ExecutiveName || exec.Name || exec.Executive || '',
          userId: exec.UserId || exec.ExecutiveId || ((exec.Executive || '').toString().includes('-') ? exec.Executive.split('-')[0] : (exec.Executive || '')),
          fullUserId: exec.Executive || exec.UserId || '',
          count: parseInt(exec.FollowupsDue || exec.FollowupDue || exec.Count || 0),
          avatar: exec.Image || exec.Avatar || ''
        };
      });

      console.log('FollowUp API Mapped Executives:', mappedExecutives);

      setFollowUpData({
        overdue: parseInt(metrics.Overdue || metrics.OverDue || 0),
        dueToday: parseInt(metrics.DueToday || metrics.Today || 0),
        dueIn7Days: parseInt(metrics.DueIn7Days || metrics.Week || 0),
        dueInThisMonth: parseInt(metrics.DueThisMonth || metrics.DueInThisMonth || metrics.Month || 0),
        executives: mappedExecutives
      });
    } catch (error) {
      console.error('Error fetching follow-up details:', error);
    } finally {
      setLoadingFollowUps(false);
    }
  };

  const fetchPipelineData = async () => {
    setLoadingPipeline(true);
    try {
      const ustrUserId = selectedUserId;
      const payload = {
        Details: {
          UserId: ustrUserId,
          strPipelineSection: "pipeline"
        },
        Message: "",
        Status: "",
        Token: TOKEN
      };

      console.log('Fetching Pipeline Data with payload:', payload);
      const response = await axios.post(Get_Pipeline_Details, payload);

      let data = response.data;
      if (data && data.d) data = data.d;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch (e) { console.error('Parse error:', e); }
      }

      // Root of data - handle object or array
      let root = data.Details || data.details || data;
      if (Array.isArray(root) && root.length > 0) root = root[0];

      console.log('Pipeline Data Root:', root);

      if (root && typeof root === 'object') {
        let stageWise = [], execWise = [], expiring = [], closures = [];

        // Collect all unique pipelines first to show in breakdown tooltips
        const allPipelinesSet = new Set();
        Object.values(root).forEach(list => {
          if (Array.isArray(list)) {
            list.forEach(item => {
              const pName = item.PipelineName || item.Pipeline || item.SourceName || item.SourceName;
              if (pName && pName !== 'Unknown') allPipelinesSet.add(pName);
            });
          }
        });
        const globalPipelines = Array.from(allPipelinesSet);

        // Greedy search through all keys to find relevant arrays
        Object.keys(root).forEach(key => {
          const list = root[key];
          if (!Array.isArray(list) || list.length === 0) return;

          const firstItem = list[0];
          const kStr = key.toLowerCase();

          if (firstItem.Stage || kStr.includes('stagewise')) {
            const grouped = {};
            list.forEach(item => {
              const label = item.Stage || item.StageName || item.Pipeline || 'Unknown';
              const pName = item.PipelineName || item.Pipeline || item.SourceName || 'Unknown';
              const val = parseInt(item.TotalLeads || item.Totalleads || item.LeadCount || 0);

              if (!grouped[label]) {
                grouped[label] = { label, value: 0, breakdown: {} };
                globalPipelines.forEach(p => grouped[label].breakdown[p] = 0);
              }
              grouped[label].value += val;
              if (pName !== 'Unknown') {
                grouped[label].breakdown[pName] = (grouped[label].breakdown[pName] || 0) + val;
              }
            });
            stageWise = Object.values(grouped).map(g => ({
              ...g,
              breakdown: Object.entries(g.breakdown).map(([name, value]) => ({ name, value }))
            }));
          } else if (firstItem.ExecutiveName || kStr.includes('executivewise')) {
            const grouped = {};
            list.forEach(item => {
              const label = item.ExecutiveName || item.Name || 'Unknown';
              const pName = item.PipelineName || item.Pipeline || item.SourceName || 'Unknown';
              const val = parseInt(item.TotalLeads || item.Totalleads || item.LeadCount || 0);

              if (!grouped[label]) {
                grouped[label] = { label, value: 0, breakdown: {} };
                globalPipelines.forEach(p => grouped[label].breakdown[p] = 0);
              }
              grouped[label].value += val;
              if (pName !== 'Unknown') {
                grouped[label].breakdown[pName] = (grouped[label].breakdown[pName] || 0) + val;
              }
            });
            execWise = Object.values(grouped).map(g => ({
              ...g,
              breakdown: Object.entries(g.breakdown).map(([name, value]) => ({ name, value }))
            }));
          } else if (firstItem.ExpiredLeads || firstItem.Expiredleads || kStr.includes('expiring')) {
            expiring = list.map(item => ({
              label: item.PipelineName || item.Pipeline || 'Unknown',
              value: parseInt(item.ExpiredLeads || item.Expiredleads || item.TotalLeads || 0),
              breakdown: [{
                name: 'Expired Deals',
                value: parseInt(item.ExpiredLeads || item.Expiredleads || item.TotalLeads || 0),
                color: '#7ecbff'
              }]
            }));
          } else if (firstItem.EstimatedClosure || firstItem.Estimatedclosure || kStr.includes('estimatedclosures')) {
            closures = list.map(item => ({
              PipelineName: item.PipelineName || item.Pipeline || 'Unknown',
              EstimatedClosure: parseFloat(item.EstimatedClosure || item.Estimatedclosure || 0)
            }));
          }
        });

        console.log('Final Mapped Data:', { stageWise, execWise, expiring, closures });
        setPipelineData({ stageWise, executiveWise: execWise, expiringDeals: expiring, estimatedClosures: closures });
      }
    } catch (error) {
      console.error('Pipeline Fetch Error:', error);
    } finally {
      setLoadingPipeline(false);
    }
  };

  const fetchEnquiryData = async () => {
    setLoadingEnquiry(true);
    setLoading(true);
    try {
      const ustrUserId = selectedUserId;
      const payload = {
        Details: {
          UserId: ustrUserId,
          strEnquirySection: "enquiries"
        },
        Message: "",
        Status: "",
        Token: TOKEN
      };

      const response = await axios.post(Get_Enquiry_Details, payload);
      let data = response.data;
      if (data && data.d) data = data.d;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch (e) { console.error('Enquiry parse error:', e); }
      }

      if (data && data.Details) {
        const details = data.Details;
        let matrix = {};
        let sourceList = [];

        Object.keys(details).forEach(key => {
          const val = details[key];
          if (Array.isArray(val) && val.length > 0) {
            if (key.toLowerCase().includes('matrix')) {
              matrix = val[0];
            } else if (val[0].Source || val[0].SourceName || val[0].Source_Name) {
              const fullList = val.map(item => ({
                name: item.Source || item.SourceName || item.Source_Name || 'Unknown',
                value: parseFloat(item.EnquiryCount || item.LeadCount || item.TotalCount || item.Value || item.count || 0)
              }));
              sourceList = fullList;
            }
          }
        });

        let finalSourceList = sourceList.sort((a, b) => b.value - a.value);

        // if (finalSourceList.length > 6) {
        //   const top6 = finalSourceList.slice(0, 6);
        //   const othersValue = finalSourceList.slice(6).reduce((acc, curr) => acc + curr.value, 0);
        //   top6.push({ name: 'Others', value: othersValue });
        //   finalSourceList = top6;
        // }

        setEnquiryData({
          today: parseInt(matrix.EnquiriesToday || matrix.Today || matrix.TodayCount || details.Today || 0),
          thisMonth: parseInt(matrix.EnquiriesThisMonth || matrix.Month || matrix.MonthCount || details.Month || 0),
          stillOpen: parseInt(matrix.StillOpen || matrix.StillOpenCount || matrix.Open || details.StillOpen || 0),
          sourceData: finalSourceList
        });
      }
    } catch (error) {
      console.error('Error fetching enquiry details:', error);
    } finally {
      setLoadingEnquiry(false);
      setLoading(false);
    }
  };

  const fetchLeadData = async () => {
    setLoadingLeads(true);
    setLoading(true);
    try {
      const ustrUserId = selectedUserId;
      const payload = {
        Details: {
          UserId: ustrUserId,
          ParentId: ustrUserId,
          strLeadSection: "leads"
        },
        Message: "",
        Status: "",
        Token: TOKEN
      };

      const response = await axios.post(Get_Lead_Details, payload);
      let data = response.data;
      if (data && data.d) data = data.d;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch (e) { console.error('Lead parse error:', e); }
      }

      if (data && data.Details) {
        const details = data.Details;
        let counters = {
          createdAssignedToday: 0,
          assignedToday: 0,
          assignedTillToday: 0,
          createdToday: 0,
          createdThisMonth: 0,
          notFollowedUpYet: 0
        };
        let topLeads = [];
        let sourceList = [];

        Object.keys(details).forEach(key => {
          const val = details[key];
          if (Array.isArray(val) && val.length > 0) {
            const lowKey = key.toLowerCase();
            if (lowKey.includes('matrix')) {
              const m = val[0];
              counters = {
                createdAssignedToday: parseInt(m.CreatedTodayAndAssignedToday || m.CreatedAndAssignedToday || m.CreatedAssignedToday || 0),
                assignedToday: parseInt(m.AssignedToday || 0),
                assignedTillToday: parseInt(m.AssignedTillToday || 0),
                createdToday: parseInt(m.LeadsToday || m.CreatedToday || 0),
                createdThisMonth: parseInt(m.LeadsThisMonth || m.CreatedInMonth || m.CreatedThisMonth || 0),
                notFollowedUpYet: parseInt(m.LeadsNotFollowedUpYet || m.NotFollowedUpYet || 0)
              };
            } else if (lowKey.includes('top') || lowKey.includes('score')) {
              topLeads = val;
            } else if (val[0].Source || val[0].SourceName || val[0].Source_Name) {
              sourceList = val.map(item => ({
                name: item.Source || item.SourceName || item.Source_Name || 'Unknown',
                value: parseFloat(item.LeadCount || item.TotalLeads || item.Count || 0)
              }));
            }
          }
        });

        let finalSourceList = sourceList.sort((a, b) => b.value - a.value);

        // if (finalSourceList.length > 6) {
        //   const top6 = finalSourceList.slice(0, 6);
        //   const othersValue = finalSourceList.slice(6).reduce((acc, curr) => acc + curr.value, 0);
        //   top6.push({ name: 'Others', value: othersValue });
        //   finalSourceList = top6;
        // }

        // Fetch real Top Leads instead of dummy data from Get_Lead_Details
        try {
          const listReportPayload = {
            Status: "",
            Message: "",
            Token: String(TOKEN),
            Details: {
              UserId: ustrUserId,
              strLeadSection: "leads"
            }
          };
          const listReportRes = await axios.post(Get_List_Report_DashBoard, listReportPayload);
          if (listReportRes.data && Array.isArray(listReportRes.data.Details) && listReportRes.data.Details.length > 0) {
            topLeads = listReportRes.data.Details;
          }
        } catch (topErr) {
          console.error("Error fetching real top leads:", topErr);
        }

        setLeadData({
          counters,
          topLeads,
          sourceData: finalSourceList
        });
      }
    } catch (error) {
      console.error('Error fetching lead details:', error);
    } finally {
      setLoadingLeads(false);
      setLoading(false);
    }
  };

  const handleTaskStatClick = (label, exec = null) => {
    const today = new Date();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const formatDateForFilter = (date) => {
      const d = date.getDate().toString().padStart(2, '0');
      const m = months[date.getMonth()];
      const y = date.getFullYear();
      return `${d}-${m}-${y}`;
    };

    const todayStr = formatDateForFilter(today);

    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    const tomorrowStr = formatDateForFilter(tomorrow);

    const sevenDaysLater = new Date();
    sevenDaysLater.setDate(today.getDate() + 7);
    const sevenDaysLaterStr = formatDateForFilter(sevenDaysLater);

    // 1st of month
    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const firstOfMonthStr = formatDateForFilter(firstOfMonth);

    // Last day of month + 1 (1st of next month)
    const firstOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const firstOfNextMonthStr = formatDateForFilter(firstOfNextMonth);

    const execName = exec ? (exec.LoginName || exec.name || '') : (localStorage.getItem('LoginName') || localStorage.getItem('username') || localStorage.getItem('UserLogin') || '');

    let queryString = "";

    if (label === 'Overdue') {
      queryString = `?FromDate=01-Jan-1900&ToDate=${todayStr}&Open=true&Overdue=true&Completed=false&Executive=${execName}`;
    } else if (label === 'Due Today') {
      queryString = `?FromDate=${todayStr}&ToDate=${tomorrowStr}&Open=true&Overdue=true&Completed=false&Executive=${execName}`;
    } else if (label === 'Due in 7 Days') {
      queryString = `?FromDate=${todayStr}&ToDate=${sevenDaysLaterStr}&Open=true&Overdue=true&Completed=false&Executive=${execName}`;
    } else if (label === 'Due this Month') {
      queryString = `?FromDate=${firstOfMonthStr}&ToDate=${firstOfNextMonthStr}&Open=true&Overdue=true&Completed=false&Executive=${execName}`;
    }

    navigate(`/tasks${queryString}`, { state: { filterLabel: label } });
  };

  const handleTaskExecutiveClick = (exec) => {
    const execName = exec.LoginName || exec.name || '';
    const queryString = `?Executive=${execName}&Open=true&Overdue=true&Completed=false`;
    navigate(`/tasks${queryString}`, {
      state: {
        filterLabel: `Executive: ${exec.name}`,
        execId: exec.userId
      }
    });
  };

  const handleFollowUpStatClick = (label) => {
    const today = new Date();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const formatDateForFilter = (date) => {
      const d = date.getDate().toString().padStart(2, '0');
      const m = months[date.getMonth()];
      const y = date.getFullYear();
      return `${d}-${m}-${y}`;
    };

    const todayStr = formatDateForFilter(today);

    const sevenDaysLater = new Date();
    sevenDaysLater.setDate(today.getDate() + 7);
    const sevenDaysLaterStr = formatDateForFilter(sevenDaysLater);

    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const endOfMonthStr = formatDateForFilter(endOfMonth);

    let filters = {
      Overdue: false,
      DueToday: false,
      Scheduled: false,
      DueThisMonth: false,
      NextStatusFromDate: "",
      NextStatusToDate: ""
    };

    if (label === 'Overdue') {
      filters.Overdue = true;
    } else if (label === 'Due Today') {
      filters.DueToday = true;
    } else if (label === 'Due in 7 Days') {
      filters.Scheduled = true;
      filters.NextStatusFromDate = todayStr;
      filters.NextStatusToDate = sevenDaysLaterStr;
    } else if (label === 'Due this Month') {
      filters.DueThisMonth = true;
    }

    navigate('/follow-ups', { state: { dashboardFilter: filters, filterLabel: label } });
  };

  const handleFollowUpExecutiveClick = (exec) => {
    const today = new Date();
    const pad = (n) => n.toString().padStart(2, '0');
    // For Dashboard redirect, use format: MM/DD/YYYY
    const todayStr = `${pad(today.getMonth() + 1)}/${pad(today.getDate())}/${today.getFullYear()}`;

    const queryParams = new URLSearchParams({
      Executive: exec.fullUserId || exec.userId,
      FromDate: '01/01/1900',
      ToDate: todayStr,
      OverDue: 'true',
      DueToday: 'false',
      Scheduled: 'false',
      NoFollowup: 'false',
      ExecutiveId: exec.userId // Pass the actual inner ID so FollowUpModule can use it for AssignedTo
    });

    navigate(`/follow-ups?${queryParams.toString()}`, {
      state: {
        filterLabel: `Executive: ${exec.name} (Overdue)`
      }
    });
  };


  const handleAppointmentStatClick = (label, exec = null) => {
    const today = new Date();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const formatDateForFilter = (date) => {
      const d = date.getDate().toString().padStart(2, '0');
      const m = months[date.getMonth()];
      const y = date.getFullYear();
      return `${d}-${m}-${y}`;
    };

    const todayStr = formatDateForFilter(today);

    // Tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    const tomorrowStr = formatDateForFilter(tomorrow);

    // 7 days from now
    const sevenDaysLater = new Date();
    sevenDaysLater.setDate(today.getDate() + 7);
    const sevenDaysLaterStr = formatDateForFilter(sevenDaysLater);

    // 1st of month
    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const firstOfMonthStr = formatDateForFilter(firstOfMonth);

    // 1st of next month
    const firstOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const firstOfNextMonthStr = formatDateForFilter(firstOfNextMonth);

    // End of month
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const endOfMonthStr = formatDateForFilter(endOfMonth);

    // Use passed exec or local storage username
    const execName = exec ? (exec.LoginName || exec.name || '') : (localStorage.getItem('LoginName') || localStorage.getItem('username') || localStorage.getItem('UserLogin') || '');

    let queryString = "";

    if (label === 'Overdue') {
      queryString = `?FromDate=${firstOfMonthStr}&ToDate=${firstOfNextMonthStr}&Open=true&Overdue=true&Completed=false&Executive=${execName}`;
    } else if (label === 'Due Today') {
      queryString = `?FromDate=${todayStr}&ToDate=${tomorrowStr}&Open=true&Overdue=true&Completed=false&Executive=${execName}`;
    } else if (label === 'Due in 7 Days') {
      queryString = `?FromDate=${todayStr}&ToDate=${sevenDaysLaterStr}&Open=true&Overdue=true&Completed=false&Executive=${execName}`;
    } else if (label === 'Due this Month') {
      // For due this month, it should pull the open appointments for this month (not overdue)
      queryString = `?FromDate=${firstOfMonthStr}&ToDate=${endOfMonthStr}&Open=true&Overdue=false&Completed=false&Executive=${execName}`;
    }

    navigate(`/appointments${queryString}`, { state: { filterLabel: label } });
  };

  const handleAppointmentExecutiveClick = (exec) => {
    const execName = exec.LoginName || exec.name || '';
    const queryString = `?Executive=${execName}&Open=true&Overdue=true&Completed=false`;
    navigate(`/appointments${queryString}`, {
      state: {
        filterLabel: `Executive: ${exec.name}`,
        execId: exec.fullUserId
      }
    });
  };

  const handleConversionStatClick = (label) => {
    const today = new Date();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const formatDateForFilter = (date) => {
      const d = date.getDate().toString().padStart(2, '0');
      const m = months[date.getMonth()];
      const y = date.getFullYear();
      return `${d}-${m}-${y}`;
    };

    const todayStr = formatDateForFilter(today);

    // Tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    const tomorrowStr = formatDateForFilter(tomorrow);

    // 1st of month
    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const firstOfMonthStr = formatDateForFilter(firstOfMonth);

    let queryString = `?Report=UserConversions`;

    if (label === 'Today' || label === 'Amount today') {
      queryString += `&OrderFromDate=${todayStr}&OrderToDate=${tomorrowStr}`;
    } else if (label === 'This month' || label === 'Amount this month') {
      queryString += `&OrderFromDate=${firstOfMonthStr}&OrderToDate=${tomorrowStr}`;
    }

    navigate(`/follow-ups${queryString}`, { state: { filterLabel: `Conversions: ${label}` } });
  };



  const handleCustomTileClick = (tile) => {
    const section = (tile.SectionName || '').toLowerCase();
    // Use ReportNameURL for pure filter name, or fallback to removing parentheses from ReportName
    const label = tile.ReportNameURL || (tile.ReportName || '').replace(/\(.*\)/, '').trim();
    const lowerLabel = label.toLowerCase();

    const isLeadLabel = lowerLabel.includes('not followed') ||
      lowerLabel.includes('not follow up') ||
      lowerLabel === 'created & assigned today' ||
      lowerLabel === 'assigned today' ||
      lowerLabel === 'assigned till today' ||
      lowerLabel === 'created today' ||
      lowerLabel === 'created this month' ||
      lowerLabel === 'createdandassignedtoday' ||
      lowerLabel === 'notfollowedupyet';

    if (section === 'leads' || section === 'lead' || isLeadLabel) {
      const filter = label.replace(/\s+/g, '');
      const isNotFollowUp = filter.toLowerCase().includes('notfollowed') || filter.toLowerCase().includes('notfollowup') ? 1 : 0;
      handleLeadNavigation(filter, isNotFollowUp);
    } else if (section === 'tasks' || section === 'task') {
      handleTaskStatClick(label);
    } else if (section === 'follow-ups' || section === 'follow-up' || section === 'followups') {
      handleFollowUpStatClick(label);
    } else if (section === 'appointments' || section === 'appointment') {
      handleAppointmentStatClick(label);
    } else if (section === 'enquiries' || section === 'enquiry') {
      let filter = label.replace(/\s+/g, '');
      const lowerEnq = label.toLowerCase();
      if (lowerEnq === 'today') filter = 'Today';
      else if (lowerEnq === 'this month') filter = 'ThisMonth';
      else if (lowerEnq === 'still open') filter = 'StillOpen';
      
      navigate(`/enquiries?filter=${filter}`);
    } else if (section === 'conversions' || section === 'conversion') {
      handleConversionStatClick(label);
    }
  };

  const handleSaveDashboardOrder = async () => {
    setSavingOrder(true);
    try {
      const ustrUserId = selectedUserId;

      const payload = {
        Status: "",
        Message: "",
        Token: String(TOKEN),
        Details: {
          UserId: ustrUserId,
          sortedTabs: sortedTabMetadata.map((tab, index) => ({
            Id: tab.Id,
            CustomDashboardOrder: index + 1,
            IsVisible: tab.IsVisible ? 1 : 0,
            CustomDashboard: tab.CustomDashboard || tab.DashboardName
          }))
        }
      };

      const response = await axios.post(SAVE_DASHBOARD_ORDER, payload);
      if (response.data) {
        await fetchDynamicTabs();
        setShowSortPanel(false);
      }
    } catch (error) {
      console.error('Error saving dashboard order:', error);
    } finally {
      setSavingOrder(false);
    }
  };

  const toggleTabVisibility = (id) => {
    setSortedTabMetadata(prev => prev.map(tab =>
      tab.Id === id ? { ...tab, IsVisible: tab.IsVisible === 0 ? 1 : 0 } : tab
    ));
  };



  return (
    <div className="dashboard-container">
      {loading && <Loader message="Loading Dashboard..." />}

      <div className="dashboard-main-header">
        <SubHeader
          pageName="Dashboard"
          title={showCustomDashboard ? "Custom Dashboard" : "Dashboard"}
          showSearch={false}
          showViewSwitcher={false}
          showFilter={false}
        >
          <div id="custom-dashboard-actions-portal" />

          {/* Hierarchy Dropdown */}
          {/* hierarchyList && hierarchyList.length > 0 && (
            <div className="hierarchy-dropdown-container" style={{ marginRight: '15px', display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '8px', fontSize: '13px', color: '#64748b', fontWeight: '500' }}>Team:</span>
              <select
                className="hierarchy-select"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: '#fff',
                  fontSize: '13px',
                  color: '#1e293b',
                  cursor: 'pointer',
                  outline: 'none',
                  minWidth: '160px',
                  fontWeight: '500',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}
              >
                {hierarchyList.map(h => (
                  <option key={h.UserID || h.UserId} value={h.UserID || h.UserId}>
                    {h.PersonName || h.Name || 'Unknown User'}
                  </option>
                ))}
              </select>
            </div>
          ) */}

          <div className="classic-actions">
            {canEdit && (
              <button
                className="manage-dashboard-btn"
                onClick={() => setShowCustomDashboard(true)}
              >
                Manage Dashboard
              </button>
            )}
            <div className="header-icon-group">
              <button
                className="sort-tabs-btn"
                onClick={() => setShowSortPanel(true)}
              >
                <FiList size={20} />
              </button>
            </div>
            <div
              className="cursor-pointer flex items-center justify-center p-2 hover:bg-gray-100 rounded-lg transition-all text-[#00b0f0]"
              onClick={() => setShowMailReport(true)}
              title="Report Mail Setting"
            >
              <ThreeGearsIcon size={20} />
            </div>
          </div>
        </SubHeader>
      </div>

      {/* Sub tabs navigation */}
      {!loading && (
        <>
          <div className="dashboard-tabs-wrapper">
            <div className="dashboard-tabs-container">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  className={`dashboard-tab ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          <div className="dashboard-tab-header">
            <h2 className="dashboard-tab-title">{activeTab}</h2>
            <div className="dashboard-tab-actions">
              <div
                className="cursor-pointer flex items-center justify-center p-2 hover:bg-gray-100 rounded-lg transition-all text-[#64748b]"
                onClick={() => setShowMailSettings(true)}
                title="Mail Settings"
              >
                <FiSettings size={21} />
              </div>
            </div>
          </div>
        </>
      )}

      <AnimatePresence>
        {showSortPanel && (
          <>
            <motion.div
              className="sort-panel-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowSortPanel(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.3)',
                zIndex: 99999,
                backdropFilter: 'blur(4px)'
              }}
            />
            <motion.div
              className="sort-panel"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: '350px',
                height: '100%',
                backgroundColor: '#fff',
                zIndex: 99999,
                boxShadow: '-10px 0 30px rgba(0,0,0,0.1)',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div className="sort-panel-header" style={{ padding: '20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 600 }}>Sort Tabs</h3>
                <button onClick={() => setShowSortPanel(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b' }}>
                  <FiX size={24} />
                </button>
              </div>

              <div className="sort-panel-body" style={{ flex: 1, overflowY: 'auto', padding: '10px', paddingBottom: '60px' }}>
                <Reorder.Group axis="y" values={sortedTabMetadata} onReorder={setSortedTabMetadata} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {sortedTabMetadata.map((tab) => (
                    <Reorder.Item
                      key={tab.Id}
                      value={tab}
                      style={{
                        padding: '12px 15px',
                        backgroundColor: '#fff',
                        borderRadius: '10px',
                        marginBottom: '8px',
                        border: '1px dashed #cbd5e1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'grab'
                      }}
                      whileDrag={{ scale: 1.02, boxShadow: '0 10px 20px rgba(0,0,0,0.05)', backgroundColor: '#f8fafc' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleTabVisibility(tab.Id);
                          }}
                          style={{
                            width: '40px',
                            height: '24px',
                            backgroundColor: tab.IsVisible === 0 ? '#e2e8f0' : '#22c55e',
                            borderRadius: '12px',
                            position: 'relative',
                            cursor: 'pointer',
                            transition: 'all 0.3s'
                          }}
                        >
                          <div style={{
                            width: '18px',
                            height: '18px',
                            backgroundColor: '#fff',
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '3px',
                            left: tab.IsVisible === 0 ? '3px' : '19px',
                            transition: 'all 0.3s'
                          }} />
                        </div>
                        <span style={{ fontSize: '0.9rem', color: '#1e293b', fontWeight: 500 }}>
                          {tab.CustomDashboard || tab.DashboardName}
                        </span>
                      </div>
                      <div style={{ color: '#94a3b8' }}>
                        <FiMenu size={18} />
                      </div>
                    </Reorder.Item>
                  ))}
                </Reorder.Group>
              </div>

              <div className="sort-panel-footer" style={{ padding: '20px', borderTop: '1px solid #f1f5f9' }}>
                <button
                  className="save-order-btn"
                  onClick={handleSaveDashboardOrder}
                  disabled={savingOrder}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#0ea5e9',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: savingOrder ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {savingOrder ? 'Saving...' : <><FiCheck /> Save changes</>}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <MailSettingsModal
        isOpen={showMailSettings}
        onClose={() => {
          setShowMailSettings(false);
          setMailEditData(null);
        }}
        editData={mailEditData}
      />

      <MailReportModal
        isOpen={showMailReport}
        onClose={() => setShowMailReport(false)}
        onAddNew={() => {
          setShowMailReport(false);
          setMailEditData(null);
          setShowMailSettings(true);
        }}
        onEdit={(data) => {
          setMailEditData(data);
          setShowMailReport(false);
          setShowMailSettings(true);
        }}
      />

      {activeTab === 'Tasks' ? (
        <div className="appt-tab-content">
          {/* Top 4 Task Stat Cards — gradient style */}
          <div className="appt-stats-grid">
            <div className="appt-stat-card appt-overdue" style={{ cursor: 'pointer' }} onClick={() => handleTaskStatClick('Overdue')}>
              <div className="appt-stat-label">Overdue</div>
              <div className="appt-stat-value">{taskData.overdue}</div>
              <div className="appt-card-icon-wrapper"><FiClock /></div>
            </div>
            <div className="appt-stat-card appt-today" style={{ cursor: 'pointer' }} onClick={() => handleTaskStatClick('Due Today')}>
              <div className="appt-stat-label">Due Today</div>
              <div className="appt-stat-value">{taskData.dueToday}</div>
              <div className="appt-card-icon-wrapper"><FiCheckSquare /></div>
            </div>
            <div className="appt-stat-card appt-week" style={{ cursor: 'pointer' }} onClick={() => handleTaskStatClick('Due in 7 Days')}>
              <div className="appt-stat-label">Due in 7 Days</div>
              <div className="appt-stat-value">{taskData.dueIn7Days}</div>
              <div className="appt-card-icon-wrapper"><FiCalendar /></div>
            </div>
            <div className="appt-stat-card appt-month" style={{ cursor: 'pointer' }} onClick={() => handleTaskStatClick('Due this Month')}>
              <div className="appt-stat-label">Due this Month</div>
              <div className="appt-stat-value">{taskData.dueInThisMonth}</div>
              <div className="appt-card-icon-wrapper"><FiCalendar /></div>
            </div>
          </div>

          <div className="appt-exec-card">
            <div className="appt-exec-header">
              <h3 className="appt-exec-title">Executive wise task due</h3>
            </div>
            <div className="appt-exec-body">
              {loadingTasks ? (
                <div className="appt-loading">Loading tasks...</div>
              ) : taskData.executives.length > 0 ? (
                <div className="executives-list">
                  {taskData.executives.map((exec, index) => (
                    <div key={index} className="executive-item appt-exec-item">
                      <div className="exec-info">
                        <div className="appt-rank" style={{ color: index < 3 ? ['#34495e', '#7d3c98', '#138d75'][index] : '#cbd5e1' }}>
                          #{index + 1}
                        </div>
                        <img
                          src={exec.avatar && !exec.avatar.includes('img_avatar1') ? exec.avatar : `https://ui-avatars.com/api/?name=${encodeURIComponent(exec.name || 'User')}&background=random&color=fff`}
                          alt="avatar"
                          className="exec-avatar"
                        />
                        <div>
                          <div
                            className="exec-name"
                            style={{ cursor: 'pointer', color: '#2563eb', fontWeight: 600 }}
                            onClick={() => handleTaskExecutiveClick(exec)}
                          >
                            {exec.name}
                          </div>
                          {exec.userId && <div className="exec-sub-id">{exec.userId}</div>}
                        </div>
                      </div>
                      <div className="appt-exec-bar-wrap">
                        <div
                          className="appt-exec-bar"
                          style={{
                            width: `${Math.min(100, (exec.count / (taskData.executives[0]?.count || 1)) * 100)}%`
                          }}
                        />
                        <span className="appt-exec-count">{exec.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="appt-loading">No executives task data available</div>
              )}
            </div>
          </div>
        </div>
      ) : activeTab === 'Appointments' ? (
        <div className="appt-tab-content">
          {/* Top 4 Appointment Stat Cards */}
          <div className="appt-stats-grid">
            <div className="appt-stat-card appt-overdue" style={{ cursor: 'pointer' }} onClick={() => handleAppointmentStatClick('Overdue')}>
              <div className="appt-stat-label">Overdue</div>
              <div className="appt-stat-value">{appointmentData.overdue}</div>
              <div className="appt-card-icon-wrapper"><FiClock /></div>
            </div>
            <div className="appt-stat-card appt-today" style={{ cursor: 'pointer' }} onClick={() => handleAppointmentStatClick('Due Today')}>
              <div className="appt-stat-label">Due Today</div>
              <div className="appt-stat-value">{appointmentData.dueToday}</div>
              <div className="appt-card-icon-wrapper"><FiCalendar /></div>
            </div>
            <div className="appt-stat-card appt-week" style={{ cursor: 'pointer' }} onClick={() => handleAppointmentStatClick('Due in 7 Days')}>
              <div className="appt-stat-label">Due in 7 Days</div>
              <div className="appt-stat-value">{appointmentData.dueIn7Days}</div>
              <div className="appt-card-icon-wrapper"><FiCalendar /></div>
            </div>
            <div className="appt-stat-card appt-month" style={{ cursor: 'pointer' }} onClick={() => handleAppointmentStatClick('Due this Month')}>
              <div className="appt-stat-label">Due this Month</div>
              <div className="appt-stat-value">{appointmentData.dueInThisMonth}</div>
              <div className="appt-card-icon-wrapper"><FiCalendar /></div>
            </div>
          </div>

          {/* Executive wise appointment list */}
          <div className="appt-exec-card">
            <div className="appt-exec-header">
              <h3 className="appt-exec-title">Executive wise Appointments</h3>
            </div>
            <div className="appt-exec-body">
              {loadingAppointments ? (
                <div className="appt-loading">Loading appointments...</div>
              ) : appointmentData.executives.length > 0 ? (
                <div className="executives-list">
                  {appointmentData.executives.map((exec, index) => (
                    <div
                      key={index}
                      className="executive-item appt-exec-item"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleAppointmentExecutiveClick(exec)}
                    >
                      <div className="exec-info">
                        <div className="appt-rank" style={{ color: index < 3 ? ['#34495e', '#7d3c98', '#138d75'][index] : '#cbd5e1' }}>
                          #{index + 1}
                        </div>
                        <img
                          src={exec.avatar && !exec.avatar.includes('img_avatar1') ? exec.avatar : `https://ui-avatars.com/api/?name=${encodeURIComponent(exec.name || 'User')}&background=random&color=fff`}
                          alt="avatar"
                          className="exec-avatar"
                        />
                        <div>
                          <div className="exec-name">{exec.name}</div>
                          {exec.userId && <div className="exec-sub-id">{exec.userId}</div>}
                        </div>
                      </div>
                      <div className="appt-exec-bar-wrap">
                        <div
                          className="appt-exec-bar"
                          style={{
                            width: `${Math.min(100, (exec.count / (appointmentData.executives[0]?.count || 1)) * 100)}%`
                          }}
                        />
                        <span className="appt-exec-count">{exec.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="appt-loading">No appointment data available</div>
              )}
            </div>
          </div>
        </div>
      ) : activeTab === 'Follow-ups' ? (
        <div className="appt-tab-content">
          {/* Top 4 Follow-up Stat Cards */}
          <div className="appt-stats-grid">
            <div className="appt-stat-card appt-overdue" style={{ cursor: 'pointer' }} onClick={() => handleFollowUpStatClick('Overdue')}>
              <div className="appt-stat-label">Overdue</div>
              <div className="appt-stat-value">{followUpData.overdue}</div>
              <div className="appt-card-icon-wrapper"><FiClock /></div>
            </div>
            <div className="appt-stat-card appt-today" style={{ cursor: 'pointer' }} onClick={() => handleFollowUpStatClick('Due Today')}>
              <div className="appt-stat-label">Due Today</div>
              <div className="appt-stat-value">{followUpData.dueToday}</div>
              <div className="appt-card-icon-wrapper"><FiCalendar /></div>
            </div>
            <div className="appt-stat-card appt-week" style={{ cursor: 'pointer' }} onClick={() => handleFollowUpStatClick('Due in 7 Days')}>
              <div className="appt-stat-label">Due in 7 Days</div>
              <div className="appt-stat-value">{followUpData.dueIn7Days}</div>
              <div className="appt-card-icon-wrapper"><FiCalendar /></div>
            </div>
            <div className="appt-stat-card appt-month" style={{ cursor: 'pointer' }} onClick={() => handleFollowUpStatClick('Due this Month')}>
              <div className="appt-stat-label">Due this Month</div>
              <div className="appt-stat-value">{followUpData.dueInThisMonth}</div>
              <div className="appt-card-icon-wrapper"><FiCalendar /></div>
            </div>
          </div>

          {/* Executive wise follow-up list */}
          <div className="appt-exec-card">
            <div className="appt-exec-header">
              <h3 className="appt-exec-title">Executive wise Follow-ups</h3>
            </div>
            <div className="appt-exec-body">
              {loadingFollowUps ? (
                <div className="appt-loading">Loading follow-ups...</div>
              ) : followUpData.executives.length > 0 ? (
                <div className="executives-list">
                  {followUpData.executives.map((exec, index) => (
                    <div key={index} className="executive-item appt-exec-item">
                      <div className="exec-info">
                        <div className="appt-rank" style={{ color: index < 3 ? ['#34495e', '#7d3c98', '#138d75'][index] : '#cbd5e1' }}>
                          #{index + 1}
                        </div>
                        <img
                          src={exec.avatar && !exec.avatar.includes('img_avatar1') ? exec.avatar : `https://ui-avatars.com/api/?name=${encodeURIComponent(exec.name || 'User')}&background=random&color=fff`}
                          alt="avatar"
                          className="exec-avatar"
                          style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover' }}
                        />
                        <div>
                          <div
                            className="exec-name"
                            style={{ cursor: 'pointer', color: '#2563eb', fontWeight: 600 }}
                            onClick={() => handleFollowUpExecutiveClick(exec)}
                          >
                            {exec.name}
                          </div>
                          {exec.userId && <div className="exec-sub-id">{exec.userId}</div>}
                        </div>
                      </div>
                      <div className="appt-exec-bar-wrap">
                        <div
                          className="appt-exec-bar"
                          style={{
                            width: `${Math.min(100, (exec.count / (followUpData.executives[0]?.count || 1)) * 100)}%`
                          }}
                        />
                        <span className="appt-exec-count">{exec.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="appt-loading">No follow-up data available</div>
              )}
            </div>
          </div>
        </div>
      ) : activeTab === 'Pipeline' ? (
        <div className="pipeline-tab-content">
          {loadingPipeline ? (
            <div className="appt-loading">Loading pipeline data...</div>
          ) : (
            <>
              <div className="pipeline-charts-grid">
                <PipelineChart
                  title="Stage wise pipeline distribution"
                  data={pipelineData.stageWise}
                  xKey="label"
                  yKey="value"
                />
                <PipelineChart
                  title="Executive wise pipeline distribution"
                  data={pipelineData.executiveWise}
                  xKey="label"
                  yKey="value"
                />
                <PipelineChart
                  title="Pipeline deal expiring distribution"
                  data={pipelineData.expiringDeals}
                  xKey="label"
                  yKey="value"
                  yAxisLabel="Expired leads"
                  legendLabel="Expired Deals"
                  barColor="#7ecbff"
                />
              </div>

              <div className="pipeline-table-card">
                <div className="pipeline-table-header">
                  <h3 className="pipeline-table-title">Pipeline wise estimated closures this month</h3>
                </div>
                <div className="pipeline-table-wrapper">
                  <table className="pipeline-summary-table">
                    <thead>
                      <tr>
                        <th>Pipeline Name</th>
                        <th style={{ textAlign: 'right' }}>Estimated Closure</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pipelineData.estimatedClosures.length > 0 ? (
                        pipelineData.estimatedClosures.map((item, idx) => (
                          <tr key={idx}>
                            <td>
                              <button
                                onClick={() => navigate('/pipeline-deal', { state: { selectedPipelineName: item.PipelineName } })}
                                className="text-blue-600 hover:text-blue-800 hover:underline font-medium transition-colors bg-transparent border-none p-0 cursor-pointer text-left"
                              >
                                {item.PipelineName}
                              </button>
                            </td>
                            <td className="text-right font-bold text-blue-600">
                              ₹ {parseFloat(item.EstimatedClosure || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="2" className="text-center py-4">No data available</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      ) : activeTab === 'Conversions' ? (
        <div className="appt-tab-content">
          {/* Top 4 Conversion Stat Cards */}
          <div className="appt-stats-grid">
            <div className="appt-stat-card appt-overdue" style={{ background: '#fef3c7', cursor: 'pointer' }} onClick={() => handleConversionStatClick('Today')}>
              <div className="appt-stat-label" style={{ color: '#92400e' }}>Today</div>
              <div className="appt-stat-value" style={{ color: '#92400e' }}>
                {conversionData?.ConversionMatrix_CLS_List?.[0]?.ConvertedToday || 0}
              </div>
              <div className="appt-card-icon-wrapper" style={{ color: '#d97706' }}><FiCheck /></div>
            </div>
            <div className="appt-stat-card appt-today" style={{ background: '#dbeafe', cursor: 'pointer' }} onClick={() => handleConversionStatClick('This month')}>
              <div className="appt-stat-label" style={{ color: '#1e40af' }}>This month</div>
              <div className="appt-stat-value" style={{ color: '#1e40af' }}>
                {conversionData?.ConversionMatrix_CLS_List?.[0]?.ConvertedThisMonth || 0}
              </div>
              <div className="appt-card-icon-wrapper" style={{ color: '#2563eb' }}><FiCalendar /></div>
            </div>
            <div className="appt-stat-card appt-week" style={{ background: '#fce7f3', cursor: 'pointer' }} onClick={() => handleConversionStatClick('Amount today')}>
              <div className="appt-stat-label" style={{ color: '#9d174d' }}>Amt today</div>
              <div className="appt-stat-value" style={{ color: '#9d174d' }}>
                {conversionData?.ConversionMatrix_CLS_List?.[0]?.AmountToday || '0.00'}
              </div>
              <div className="appt-card-icon-wrapper" style={{ color: '#db2777' }}><FiArrowUpRight /></div>
            </div>
            <div className="appt-stat-card appt-month" style={{ background: '#f3f4f6', cursor: 'pointer' }} onClick={() => handleConversionStatClick('Amount this month')}>
              <div className="appt-stat-label" style={{ color: '#374151' }}>Amt this month</div>
              <div className="appt-stat-value" style={{ color: '#374151' }}>
                {conversionData?.ConversionMatrix_CLS_List?.[0]?.AmountThisMonth || '0.00'}
              </div>
              <div className="appt-card-icon-wrapper" style={{ color: '#4b5563' }}><FiArrowUpRight /></div>
            </div>
          </div>

          {/* Top Scoring Executives */}
          <div className="appt-exec-card">
            <div className="appt-exec-header">
              <h3 className="appt-exec-title">Top scoring executives this month</h3>
            </div>
            <div className="appt-exec-body">
              {loadingConversion ? (
                <div className="appt-loading">Loading conversions...</div>
              ) : conversionData?.ConversionTopExecutiveAmount_CLS_List?.length > 0 ? (
                <div className="executives-list">
                  {conversionData.ConversionTopExecutiveAmount_CLS_List.map((exec, index) => (
                    <div
                      key={index}
                      className="executive-item appt-exec-item"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const today = new Date();
                        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                        const formatDateForFilter = (date) => {
                          const d = date.getDate().toString().padStart(2, '0');
                          const m = months[date.getMonth()];
                          const y = date.getFullYear();
                          return `${d}-${m}-${y}`;
                        };
                        const firstOfMonthStr = formatDateForFilter(new Date(today.getFullYear(), today.getMonth(), 1));
                        const tomorrow = new Date(); tomorrow.setDate(today.getDate() + 1);
                        const tomorrowStr = formatDateForFilter(tomorrow);
                        const userId = exec.UserId || exec.ExecutiveId || exec.userId || '';
                        navigate(`/follow-ups?Report=UserConversions&CreatedBy=${userId}&OrderFromDate=${firstOfMonthStr}&OrderToDate=${tomorrowStr}`, {
                          state: { filterLabel: `Top Executive: ${exec.ExecutiveName}` }
                        });
                      }}
                    >
                      <div className="exec-info">
                        <div className="appt-rank" style={{ color: index < 3 ? ['#34495e', '#7d3c98', '#138d75'][index] : '#cbd5e1' }}>
                          #{index + 1}
                        </div>
                        <img
                          src={exec.Image && !exec.Image.includes('img_avatar1') ? exec.Image : `https://ui-avatars.com/api/?name=${encodeURIComponent(exec.ExecutiveName || 'User')}&background=random&color=fff`}
                          alt="avatar"
                          className="exec-avatar"
                          style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover' }}
                        />
                        <div>
                          <div className="exec-name" style={{ color: '#1e293b', fontWeight: 600 }}>
                            {exec.ExecutiveName}
                          </div>
                          {exec.Executive && <div className="exec-sub-id">{exec.Executive}</div>}
                        </div>
                      </div>
                      <div className="appt-exec-bar-wrap">
                        <div
                          className="appt-exec-bar"
                          style={{
                            background: '#2563eb',
                            width: `${Math.min(100, (parseFloat(exec.Amount) / parseFloat(conversionData.ConversionTopExecutiveAmount_CLS_List[0]?.Amount || 1)) * 100)}%`
                          }}
                        />
                        <span className="appt-exec-count" style={{ color: '#2563eb', fontWeight: 'bold' }}>
                          ₹ {parseFloat(exec.Amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="appt-loading">No conversion data available</div>
              )}
            </div>
          </div>
        </div>
      ) : activeTab.toLowerCase().includes('lead') ? (
        <div className="appt-tab-content">
          {/* Leads Stat Cards - 6 Columns */}
          <div className="appt-stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            <div className="appt-stat-card appt-overdue" style={{ cursor: 'pointer' }} onClick={() => handleLeadNavigation('CreatedAndAssignedToday')}>
              <div className="appt-stat-label">Created & Assigned Today</div>
              <div className="appt-stat-value">{leadData.counters.createdAssignedToday}</div>
              <div className="appt-card-icon-wrapper"><FiUsers /></div>
            </div>
            <div className="appt-stat-card appt-active" style={{ cursor: 'pointer' }} onClick={() => handleLeadNavigation('AssignedToday')}>
              <div className="appt-stat-label">Assigned Today</div>
              <div className="appt-stat-value">{leadData.counters.assignedToday}</div>
              <div className="appt-card-icon-wrapper"><FiUsers /></div>
            </div>
            <div className="appt-stat-card appt-today" style={{ cursor: 'pointer' }} onClick={() => handleLeadNavigation('AssignedTillToday')}>
              <div className="appt-stat-label">Assigned Till Today</div>
              <div className="appt-stat-value">{leadData.counters.assignedTillToday}</div>
              <div className="appt-card-icon-wrapper"><FiUsers /></div>
            </div>
            <div className="appt-stat-card appt-week" style={{ cursor: 'pointer' }} onClick={() => handleLeadNavigation('CreatedToday')}>
              <div className="appt-stat-label">Created Today</div>
              <div className="appt-stat-value">{leadData.counters.createdToday}</div>
              <div className="appt-card-icon-wrapper"><FiFileText /></div>
            </div>
            <div className="appt-stat-card appt-month" style={{ cursor: 'pointer' }} onClick={() => handleLeadNavigation('CreatedThisMonth')}>
              <div className="appt-stat-label">Created this month</div>
              <div className="appt-stat-value">{leadData.counters.createdThisMonth}</div>
              <div className="appt-card-icon-wrapper"><FiCalendar /></div>
            </div>
            <div className="appt-stat-card appt-other" style={{ cursor: 'pointer' }} onClick={() => handleLeadNavigation('NotFollowedUpYet')}>
              <div className="appt-stat-label">Not followed up yet</div>
              <div className="appt-stat-value">{leadData.counters.notFollowedUpYet}</div>
              <div className="appt-card-icon-wrapper"><FiClock /></div>
            </div>
          </div>

          <div className="pipeline-charts-grid" style={{ marginTop: '24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            {/* Top 10 Leads List */}
            <div className="pipeline-chart-card" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
              <div className="pipeline-chart-header" style={{ marginBottom: '1.25rem' }}>
                <h3 className="pipeline-chart-title" style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' }}>Top 10 leads with highest score()</h3>
              </div>
              <div className="executives-list" style={{ flex: 1, overflowY: 'auto', paddingRight: '10px' }}>
                {loadingLeads ? (
                  <div className="appt-loading">Loading top leads...</div>
                ) : leadData.topLeads.length > 0 ? (
                  leadData.topLeads.slice(0, 10).map((lead, idx) => (
                    <div key={idx} className="executive-item appt-exec-item" style={{
                      padding: '12px 0',
                      borderBottom: '1px solid #f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer'
                    }}
                      onClick={() => {
                        const trueId = lead.LeadId || lead.ID || lead.Id || lead.id || lead.Lead_Id || lead.LeadID;
                        if (trueId) {
                          handleLeadNavigation(`ID = '${trueId}'`);
                        } else if (lead.LeadNo) {
                          handleLeadNavigation(`LeadNo = '${lead.LeadNo}'`);
                        }
                      }}
                    >
                      <div className="exec-info" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '24px', textAlign: 'center', fontWeight: 'bold', color: '#94a3b8', fontSize: '0.8rem' }}>
                          #{idx + 1}
                        </div>
                        <img
                          src={`https://ui-avatars.com/api/?name=${encodeURIComponent(getSafeVal(lead, ['PersonName', 'LeadName', 'Name'], 'L'))}&background=random&color=fff`}
                          alt="avatar"
                          className="exec-avatar"
                          style={{ width: '36px', height: '36px', borderRadius: '8px' }}
                        />
                        <div style={{ minWidth: 0 }}>
                          <div className="exec-name" style={{ fontSize: '0.9rem', fontWeight: 600, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {getSafeVal(lead, ['PersonName', 'LeadName', 'Name'], 'Unknown')}
                          </div>
                          <div className="exec-sub-id" style={{ fontSize: '0.75rem', color: '#64748b' }}>Lead No. {lead.LeadNo}</div>
                        </div>
                      </div>
                      <div style={{
                        padding: '4px 10px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '6px',
                        fontWeight: 700,
                        color: '#0f172a',
                        fontSize: '0.9rem',
                        border: '1px solid #e2e8f0'
                      }}>
                        {getSafeVal(lead, ['Currentscore', 'CurrentScore', 'LeadScore', 'Lead_Score', 'TotalScore', 'Score', 'HighestScore'], 0)}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="pipeline-no-data">No lead score data found</div>
                )}
              </div>
            </div>

            {/* Source Wise Lead Distribution Pie Chart */}
            <div className="pipeline-chart-card" style={{ padding: '1.5rem' }}>
              <div className="pipeline-chart-header" style={{ justifyContent: 'center', marginBottom: '1.25rem' }}>
                <h3 className="pipeline-chart-title" style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' }}>Source Wise Lead Distribution</h3>
              </div>
              <div className="pipeline-chart-recharts-container" style={{ height: 520 }}>
                {loadingLeads ? (
                  <div className="appt-loading">Loading lead sources...</div>
                ) : leadData.sourceData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                      <Pie
                        activeIndex={activePieIndex}
                        activeShape={renderActivePieShape}
                        data={leadData.sourceData}
                        cx="50%"
                        cy="50%"
                        startAngle={90}
                        endAngle={-270}
                        labelLine={false}
                        label={renderLeadPieLabel}
                        outerRadius={100}
                        innerRadius={0}
                        minAngle={3}
                        dataKey="value"
                        onMouseEnter={onPieEnter}
                        onMouseLeave={onPieLeave}
                      >
                        {leadData.sourceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={[
                            '#1e3a8a', '#7c3aed', '#10b981', '#f97316', '#eab308', '#ec4899',
                            '#06b6d4', '#6366f1', '#14b8a6', '#f43f5e', '#8b5cf6', '#d946ef'
                          ][index % 12]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                        formatter={(value, name, props) => {
                          const total = leadData.sourceData.reduce((acc, curr) => acc + curr.value, 0);
                          const percentage = ((value / total) * 100).toFixed(1);
                          return [`${value} (${percentage}%)`, name];
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="pipeline-no-data">No lead source data found</div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : activeTab.toLowerCase().includes('enquir') ? (
        <div className="appt-tab-content">
          {/* Enquiry Stat Cards */}
          <div className="appt-stats-grid">
            <div className="appt-stat-card appt-overdue" style={{ cursor: 'pointer' }} onClick={() => navigate('/enquiries?filter=Today')}>
              <div className="appt-stat-label">Today</div>
              <div className="appt-stat-value">{enquiryData.today}</div>
              <div className="appt-card-icon-wrapper"><FiClock /></div>
            </div>
            <div className="appt-stat-card appt-active" style={{ cursor: 'pointer' }} onClick={() => navigate('/enquiries?filter=ThisMonth')}>
              <div className="appt-stat-label">This month</div>
              <div className="appt-stat-value">{enquiryData.thisMonth}</div>
              <div className="appt-card-icon-wrapper"><FiCalendar /></div>
            </div>
            <div className="appt-stat-card appt-today" style={{ cursor: 'pointer' }} onClick={() => navigate('/enquiries?filter=StillOpen')}>
              <div className="appt-stat-label">Still Open</div>
              <div className="appt-stat-value">{enquiryData.stillOpen}</div>
              <div className="appt-card-icon-wrapper"><FiMail /></div>
            </div>
          </div>

          <div className="pipeline-charts-grid" style={{ gridTemplateColumns: 'minmax(0, 1fr)', marginTop: '24px' }}>
            <div className="pipeline-chart-card" style={{ padding: '2rem' }}>
              <div className="pipeline-chart-header" style={{ justifyContent: 'center', marginBottom: '4rem' }}>
                <h3 className="pipeline-chart-title" style={{ fontSize: '1.25rem' }}>Source Wise Enquiry Distribution</h3>
              </div>
              <div className="pipeline-chart-recharts-container" style={{ height: 450, marginTop: '20px' }}>
                {loadingEnquiry ? (
                  <div className="appt-loading">Loading enquiry sources...</div>
                ) : enquiryData.sourceData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        activeIndex={activePieIndex}
                        activeShape={renderActivePieShape}
                        data={enquiryData.sourceData}
                        cx="50%"
                        cy="50%"
                        startAngle={90}
                        endAngle={-270}
                        labelLine={false}
                        label={renderLeadPieLabel}
                        outerRadius={100}
                        innerRadius={0}
                        minAngle={3}
                        dataKey="value"
                        onMouseEnter={onPieEnter}
                        onMouseLeave={onPieLeave}
                      >
                        {enquiryData.sourceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={[
                            '#1e3a8a', '#7c3aed', '#10b981', '#f97316', '#eab308',
                            '#ec4899', '#06b6d4', '#6366f1', '#14b8a6', '#f43f5e'
                          ][index % 10]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                        formatter={(value, name, props) => {
                          const total = enquiryData.sourceData.reduce((acc, curr) => acc + curr.value, 0);
                          const percentage = ((value / total) * 100).toFixed(1);
                          return [`${value} (${percentage}%)`, name];
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="pipeline-no-data">No enquiry source data found</div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="custom-dashboard-container" ref={containerRef}>
          {loadingTiles ? null : customDashboardTiles.length > 0 ? (
            <GridLayout
              className="layout"
              layout={customDashboardTiles.map((tile, idx) => ({
                i: String(idx),
                x: tile.PositionX || 0,
                y: tile.PositionY || 0,
                w: (tile.ReportName && tile.ReportName.toLowerCase().includes('top 10 leads with highest score'))
                  ? Math.max(tile.Width || 3, 3)
                  : (tile.Width || 2),
                h: tile.Height || 1,
                static: true
              }))}
              cols={12}
              rowHeight={100}
              width={width || 1200}
              margin={[20, 20]}
              containerPadding={[20, 20]}
            >
              {customDashboardTiles.map((tile, idx) => (
                <div key={idx} className="custom-widget-card">
                  <CustomWidgetContent
                    onTileClick={handleCustomTileClick}
                    onListItemClick={(item, tile) => {
                      const section = (tile.SectionName || '').toLowerCase();
                      const reportName = (tile.ReportName || '').replace(/\s+/g, '').toLowerCase();
                      const isLeadSection = section === 'leads' || section === 'lead' || reportName.includes('top10leads');
                      const isEnquirySection = section === 'enquiries' || section === 'enquiry' || reportName.includes('top10enquiries');
                      
                      if (isLeadSection) {
                        const trueId = item.LeadId || item.ID || item.Id || item.id || item.Lead_Id || item.LeadID;
                        const isNotFollowUp = reportName.includes('notfollowed') || reportName.includes('notfollowup') ? 1 : 0;
                        if (trueId) {
                          handleLeadNavigation(`ID = '${trueId}'`, isNotFollowUp);
                        } else if (item.LeadNo) {
                          handleLeadNavigation(`LeadNo = '${item.LeadNo}'`, isNotFollowUp);
                        }
                      } else if (isEnquirySection) {
                        const trueId = item.EnquiryId || item.ID || item.Id || item.id || item.LeadId;
                        if (trueId) {
                          navigate(`/enquiries?filter=EnquiryId='${trueId}'`);
                        }
                      }
                    }}
                    tile={tile}
                    data={(customDashboardData[(tile.ReportName || '').trim()] && customDashboardData[(tile.ReportName || '').trim()].length > 0)
                      ? customDashboardData[(tile.ReportName || '').trim()]
                      : (customDashboardData[tile.SectionName] || [])}
                    pieData={(sourcePieData[(tile.ReportName || '').trim()] && sourcePieData[(tile.ReportName || '').trim()].length > 0)
                      ? sourcePieData[(tile.ReportName || '').trim()]
                      : (sourcePieData[tile.SectionName] || [])}
                  />
                </div>
              ))}
            </GridLayout>
          ) : !loading ? (
            <div className="tab-placeholder">
              <p>No widgets found for {activeTab}. Configure it in Manage Dashboard.</p>
            </div>
          ) : null}
        </div>
      )}

      {showCustomDashboard && (
        <CustomDashboard onClose={() => setShowCustomDashboard(false)} />
      )}

      {/* Todo Panel Toggle Icon */}
      {!showTodoPanel && (
        <button
          className="todo-panel-toggle-btn"
          onClick={() => setShowTodoPanel(true)}
          title="Open To-Do & Scrapbook"
        >
          <FiFileText size={24} />
        </button>
      )}

      {/* Todo & Scrapbook Floating Panel */}
      <AnimatePresence>
        {showTodoPanel && (
          <motion.div
            className="todo-scrapbook-panel"
            initial={{ opacity: 0, scale: 0.8, x: 100, y: 100 }}
            animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, x: 100, y: 100 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <div className="todo-panel-header">
              <div className="todo-panel-tabs">
                <button
                  className={`todo-panel-tab ${activeTodoTab === 'todo' ? 'active' : ''}`}
                  onClick={() => setActiveTodoTab('todo')}
                >
                  To-Do List
                </button>
                <button
                  className={`todo-panel-tab ${activeTodoTab === 'scrapbook' ? 'active' : ''}`}
                  onClick={() => setActiveTodoTab('scrapbook')}
                >
                  Scrapbook
                </button>
              </div>
              <button className="todo-panel-close" onClick={() => setShowTodoPanel(false)}>
                <FiX size={20} />
              </button>
            </div>

            <div className="todo-panel-content">
              {activeTodoTab === 'todo' ? (
                <div className="todo-tab-view">
                  <div className="todo-date-selector">
                    <button onClick={handlePrevDate} className="date-nav-btn"><FiChevronLeft /></button>
                    <span className="selected-date">{formatTodoDate(todoDate)}</span>
                    <button onClick={handleNextDate} className="date-nav-btn"><FiChevronRight /></button>
                  </div>

                  <div className="todo-categories">
                    {loadingCalendar ? (
                      <div className="appt-loading" style={{ padding: '20px', textAlign: 'center' }}>Loading calendar...</div>
                    ) : calendarData ? (
                      Object.keys(calendarData).map((categoryName, idx) => {
                        const categoryStats = calendarData[categoryName];
                        if (typeof categoryStats === 'object' && categoryStats !== null) {
                          return (
                            <div className="todo-category-card" key={idx}>
                              <div className="category-header">
                                <span className="category-name">{categoryName}</span>
                                <FiPlus className="category-plus" />
                              </div>
                              <div className="category-stats">
                                {Array.isArray(categoryStats) ? (
                                  <span className="stat-item">{categoryStats.length} Items</span>
                                ) : (
                                  Object.entries(categoryStats).map(([statName, statValue], sIdx) => {
                                    if (typeof statValue === 'object') return null;
                                    return (
                                      <span className="stat-item" key={sIdx}>{statValue} {statName}</span>
                                    );
                                  })
                                )}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })
                    ) : (
                      <div className="appt-loading" style={{ padding: '20px', textAlign: 'center' }}>No calendar data</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="scrapbook-tab-view">
                  <div className="scrapbook-controls">
                    <div className="scrapbook-search">
                      <FiSearch size={18} />
                    </div>
                    <div className="scrapbook-add-wrapper">
                      <button
                        className="scrapbook-add-btn"
                        onClick={() => setShowScrapbookAddMenu(!showScrapbookAddMenu)}
                      >
                        <FiPlus size={20} />
                      </button>
                      {showScrapbookAddMenu && (
                        <div className="scrapbook-add-menu">
                          <button onClick={() => { setShowScrapbookAddMenu(false); setShowAddNoteModal(true); }}>Notes</button>
                          <button onClick={() => { setShowScrapbookAddMenu(false); setShowAddChecklistModal(true); }}>Checklist</button>
                          <button onClick={() => { setShowScrapbookAddMenu(false); setShowMediaLibrary(true); }}>Upload</button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="scrapbook-grid" style={{ position: 'relative' }}>
                    {loadingScrapbook ? (
                      <div className="appt-loading" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px' }}>Loading scrapbook...</div>
                    ) : scrapbookItems.length > 0 ? (
                      scrapbookItems.map(item => (
                        <div
                          key={item.ScrapBookId || item.id || Math.random()}
                          className={`scrapbook-item ${item.ItemType ? item.ItemType.toLowerCase() : 'note'}`}
                          style={{ backgroundColor: (item.Theme || '').split('-')[0] || item.color || '#f8fafc', display: 'flex', flexDirection: 'column' }}
                        >
                          <div className="item-header">
                            <span className="item-title">
                              {item.Title || item.title || (item.ItemType === 'Attachment' && item.Note && item.Note.substring(item.Note.lastIndexOf('/') + 1)) || (item.ItemType === 'CheckList' && item.CheckList && item.CheckList[0]?.Content) || 'Untitled'}
                            </span>
                            <FiMoreHorizontal />
                          </div>

                          {item.ItemType === 'CheckList' ? (
                            <div style={{ fontSize: '13px', marginTop: '6px', opacity: 0.8 }}>
                              {item.CheckList && item.CheckList.length > 0 ? (
                                item.CheckList.map((cl, i) => (
                                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                    <FiCheckSquare size={14} /> <span>{cl.Content}</span>
                                  </div>
                                ))
                              ) : (
                                <div><FiCheckSquare style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Checklist</div>
                              )}
                            </div>
                          ) : item.ItemType === 'Attachment' ? (
                            <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                              {isImageUrl(item.Note || '') ? (
                                <div style={{ width: '100%', height: '100px', borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.05)' }}>
                                  <img
                                    src={item.Note}
                                    alt={item.Title}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    onClick={(e) => { e.stopPropagation(); window.open(item.Note, '_blank'); }}
                                  />
                                </div>
                              ) : (
                                <div
                                  onClick={(e) => { e.stopPropagation(); window.open(item.Note, '_blank'); }}
                                  style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', background: 'rgba(255,255,255,0.5)', borderRadius: '8px', cursor: 'pointer', border: '1px solid rgba(0,0,0,0.05)', transition: 'all 0.2s' }}
                                  className="attachment-link-hover"
                                >
                                  <FiFileText size={20} style={{ color: '#2563eb' }} />
                                  <span style={{ fontSize: '12px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '140px' }}>
                                    {item.Title || 'Attachment'}
                                  </span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div style={{ fontSize: '13px', marginTop: '6px', opacity: 0.9, whiteSpace: 'pre-wrap', wordBreak: 'break-word', flex: 1 }}>
                              {item.Note || item.content || ''}
                            </div>
                          )}

                          <div className="item-date" style={{ marginTop: 'auto', paddingTop: '12px' }}>{item.CreatedDate || item.date || ''}</div>
                        </div>
                      ))
                    ) : (
                      <div className="appt-loading" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px' }}>No items found</div>
                    )}
                  </div>

                  {showAddNoteModal && (
                    <div className="scrapbook-modal-overlay" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.9)', zIndex: 10, display: 'flex', flexDirection: 'column', padding: '20px' }}>
                      <div className="scrapbook-modal-content" style={{ background: noteTheme.split('-')[0] || '#e8b01c', borderRadius: '12px', padding: '16px', position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}>
                        <button style={{ position: 'absolute', right: '12px', top: '12px', background: 'transparent', border: 'none', cursor: 'pointer' }} onClick={() => setShowAddNoteModal(false)}>
                          <FiX size={20} color="#333" />
                        </button>
                        <input
                          type="text"
                          placeholder="title"
                          value={noteTitle}
                          onChange={(e) => setNoteTitle(e.target.value)}
                          style={{ background: 'transparent', border: 'none', fontSize: '18px', fontWeight: 'bold', color: '#333', outline: 'none', marginBottom: '16px', width: '80%' }}
                        />
                        <textarea
                          placeholder="Note"
                          value={noteContent}
                          onChange={(e) => setNoteContent(e.target.value)}
                          style={{ flex: 1, background: 'transparent', border: 'none', resize: 'none', outline: 'none', color: '#333', fontSize: '14px', lineHeight: '1.5' }}
                        />
                        <div style={{ borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: '12px', marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <button style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', color: '#333' }} onClick={() => handleSaveScrapbookItem('Notes')}>A</button>
                          <button style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }} onClick={() => handleSaveScrapbookItem('Notes')} title="Save Note">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
                          </button>
                          <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
                            {['#e8b01c', '#82e0d0', '#e74c3c', '#f1c40f', '#d9e8f0'].map(color => (
                              <div key={color} onClick={() => setNoteTheme(color)} style={{ width: '16px', height: '16px', borderRadius: '50%', background: color, cursor: 'pointer', border: noteTheme.startsWith(color) ? '2px solid #333' : '1px solid rgba(0,0,0,0.2)' }} />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {showAddChecklistModal && (
                    <div className="scrapbook-modal-overlay" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.9)', zIndex: 10, display: 'flex', flexDirection: 'column', padding: '20px' }}>
                      <div className="scrapbook-modal-content" style={{ background: checklistTheme.split('-')[0] || '#d9e8f0', borderRadius: '12px', padding: '16px', position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}>
                        <button style={{ position: 'absolute', right: '12px', top: '12px', background: 'transparent', border: 'none', cursor: 'pointer' }} onClick={() => setShowAddChecklistModal(false)}>
                          <FiX size={20} color="#333" />
                        </button>
                        <input
                          type="text"
                          placeholder="title"
                          value={checklistTitle}
                          onChange={(e) => setChecklistTitle(e.target.value)}
                          style={{ background: 'transparent', border: 'none', fontSize: '18px', fontWeight: 'bold', color: '#333', outline: 'none', marginBottom: '16px', width: '80%' }}
                        />

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                          <input type="checkbox" disabled style={{ width: '16px', height: '16px', accentColor: '#333' }} />
                          <input
                            type="text"
                            placeholder="New Item"
                            value={checklistNewItem}
                            onChange={(e) => setChecklistNewItem(e.target.value)}
                            style={{ flex: 1, background: 'rgba(255,255,255,0.4)', border: 'none', padding: '10px 12px', borderRadius: '6px', outline: 'none', color: '#333', fontSize: '14px' }}
                          />
                        </div>

                        <div style={{ marginTop: 'auto', borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <button style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }} onClick={() => handleSaveScrapbookItem('CheckList')} title="Save Checklist">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
                          </button>
                          <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
                            {['#d9e8f0', '#e8b01c', '#82e0d0', '#e74c3c', '#f1c40f'].map(color => (
                              <div key={color} onClick={() => setChecklistTheme(color)} style={{ width: '16px', height: '16px', borderRadius: '50%', background: color, cursor: 'pointer', border: checklistTheme.startsWith(color) ? '2px solid #333' : '1px solid rgba(0,0,0,0.2)' }} />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {showMediaLibrary && (
                    <MediaLibrary
                      show={showMediaLibrary}
                      onclose={() => setShowMediaLibrary(false)}
                      onSelectMedia={handleSelectMediaForScrapbook}
                      filterOption="Images"
                    />
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dashboard;


